/* dashboard-freight-cards.js — the Deal Dashboard's FREIGHT QUOTE and INSTALLATION QUOTE cards.
   CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-08-29)

   ⛔ THE GAP THESE FILL. Entering a freight or installation cost on an offline deal meant opening
   the whole quote in the Quote Workstation. The dashboard already had freight controls — a
   per-vendor cost box and a Weight/dims row — but they live inside the PURCHASING card, which is
   gated on RQD.dealGoodsAreCommitted(q), i.e. SOLD or replacement. A rep who requests freight on a
   QUOTED deal produced no card at all, so there was nowhere to answer from. Installation had no
   cost surface anywhere in the DD: the existing Installation card is a SCHEDULING card that needs
   financials.installCost > 0 just to appear, so it can only show up after the number was entered
   somewhere else.

   ⛔ THE GATE WAS THE PURCHASING CARD'S, INVERTED — deliberately, so the two could never both draw
   the same step. Purchasing rendered when dealGoodsAreCommitted(q); these rendered when it was
   false. Ryan's call (2026-08-29): leave the Purchasing rows alone, these cover the window before
   SOLD.

   ⚠️ THAT IS NO LONGER HOW IT WORKS, AND THE PARAGRAPH ABOVE IS KEPT ONLY AS HISTORY (2026-08-30).
   These cards render after the sale too — the carrier's corrected rate arrives weeks later, which
   is precisely when the inverted gate used to remove them. The invariant did not lapse; it moved
   to the OTHER side: the Purchasing card now stands its freight-cost INPUT down while these are on
   screen and shows the figure as a value instead. See the long note on `cardsApply` below for why
   the Purchasing box was never a substitute — it posts a bare cost, which silently re-prices the
   customer — and dashboard-po-lens.js for the row that yields.

   ⛔ WHAT THEY WRITE, AND WHY IT IS NOT freightQuotes. On an OFFLINE deal the served
   financials.freightCost resolves: our-carrier override > wm.financials.freightCost >
   wm.freight.totalCost. The lc.freightQuotes[v].cost sum lives inside the `source === 'online'`
   block of quote-summary-builder and never runs offline. So these cards drive
   workstation_meta.freight / .installation — the SAME object the Workstation edits, which is what
   makes the sync two-way rather than two stores pretending to agree. Install has no lifecycle
   store at all.

   ⚠️ CARRIER AND TYPE ARE NOT DECORATION. The Workstation's Apply gate (freightVendorIsSet,
   workstation-freight.js:1051) refuses any vendor without both, which is why a cost saved from the
   old dashboard box landed in the QW as a row the QW could not apply. Every save from this card
   sends them.

   Reads q.wsFreight / q.wsInstallation (lib/ws-logistics.js projections, baked into dd-summary).
   ⚠️ A deal shows nothing here until its summary re-bakes — every mutation endpoint bakes, and the
   0 6,18 cron sweeps the rest. Force one with GET /admin/dd-summary-bake?ref=X.
*/
(function () {
  'use strict';
  window.RQD = window.RQD || {};

  var esc, money, formatDate, sectionTitleHtml, getIsAdmin, API,
      getAllQuotes, rerenderDetail, refreshProgressBar, notePendingPatch, vendorNamesMatch,
      fetchQuotesQuiet,
      /* CUSTOM_DDINSTALL_BY_CLAUDE (Ryan 2026-09-02) - the closeout-row renderer, injected rather
         than reimplemented: Installer paid must look and behave exactly like Taxes Remitted. */
      buildToggle;

  var TYPE_CYCLE = { unset: 'estimate', estimate: 'official', official: 'unset' };
  var TYPE_GLYPH = { unset: '?', estimate: 'E', official: '✓' };
  var TYPE_TITLE = {
    unset: 'Not set — click to mark this an estimate',
    estimate: 'Estimate — click to mark it official',
    official: 'Official quote — click to clear',
  };

  /* The five order-level delivery conditions and the two install scopes, mirroring
     lib/ws-logistics.js on the worker and sections/rep-quote-workstation.liquid in the QW.
     ⚠️ Order matters only for reading; the KEYS must match the worker's allow-list exactly or the
     write is refused with "Unknown delivery flag". */
  var DELIVERY_FLAGS = [
    { key: 'loadingDock', label: 'Loading dock' },
    { key: 'forkLift', label: 'Fork-lift' },
    { key: 'insideDelivery', label: 'Inside delivery' },
    { key: 'freightInsurance', label: 'Freight insurance' },
    { key: 'defaultDelivery', label: 'Curbside / driveway' },
  ];

  /* The Workstation's carrier list (workstation-freight.js:23), verbatim.
     ⚠️ NOT the QW's localStorage history — that is per-browser, and an admin working a deal from the
     dashboard would get a different set of suggestions than the same admin in the Workstation on
     another machine. The presets are the part that is actually shared. The field stays free text,
     so a carrier not on this list is typed, not blocked. */
  /* The Workstation caps installers at two (workstation-freight.js:1815). Named once so the
     renderer and the in-place insert cannot disagree about when the add row goes away. */
  var MAX_INSTALLERS = 2;

  /* CUSTOM_CARRIERNAMES_BY_CLAUDE (ONYX 609f1d1514ca, Ryan 2026-10-16) - three FedEx choices replace the ambiguous
     'FedEx Priority' (a freight truck and an overnight parcel read the same). Kept in step with workstation-freight.js. */
  var CARRIER_PRESETS = [
    'FedEx Freight', 'FedEx Freight Priority', 'FedEx Freight Economy', 'FedEx Ground', 'FedEx Priority Overnight', 'ABF Freight', 'XPO Logistics',
    'Estes Express', 'Old Dominion', 'SAIA', 'R+L Carriers', 'Daylight Transport',
    'YRC Freight', 'Forward Air', 'Go Rocket Shipping', 'Other',
  ];

  function n2(v) { var f = parseFloat(v); return isFinite(f) ? Math.round(f * 100) / 100 : 0; }
  function fixed2(v) { return n2(v).toFixed(2); }

  /* ⛔ THIS IS THE PURCHASING CARD'S GATE, INVERTED. Keeping it as one expression here (rather than
     letting buildDetailHtml decide) is what guarantees the two cards are complementary: if
     Purchasing's condition ever moves, this reads the same helper and moves with it.

     📌 getIsAdmin() rather than main's `_effectiveAdmin`: _effectiveAdmin only differs from isAdmin
     when q.source === 'online', which this gate has already excluded. Equivalent here, and one
     fewer dep to keep in sync. */
  /* CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-08-30) — ⛔ THE SOLD EXCLUSION IS GONE, AND THE
     INVERSE-GATE RULE ABOVE IS NOW HONOURED ON THE OTHER SIDE INSTEAD.

     Ryan: *"What will happen after a deal sells (with many vendors) is that we'll eventually get
     the corrected freight amount from our shipper and we'll want to go in and update our cost that
     we recorded during the quoting phase as the new cost, then we press the 'keep' button to ensure
     we're still charging the same thing… right now, we have to click into the deal via the QW to
     record this number."*

     ⛔ THE REAL RATE ARRIVES AFTER THE SALE, WHICH IS EXACTLY WHEN THIS CARD USED TO VANISH. The
     card was built for the window BEFORE sold, and the carrier's true number lands weeks after it —
     so the one surface with the right controls disappeared at the moment they were needed.

     📌 AND THE PURCHASING CARD IS NOT A SUBSTITUTE, FOR A REASON THAT COSTS MONEY. Its freight-cost
     box posts a bare `{quoteRef, vendor, cost}`. `/freight-cost-save` treats an absent `charged` as
     "leave it", so the row's forward recompute re-derives `markedUpTotal` from the stored MARKUP —
     i.e. updating the cost there silently moves what the CUSTOMER is charged. This card carries the
     charged box that back-solves the markup and sets `_chargedOverride`, which is precisely Ryan's
     "keep" — the number we bill stays put while the cost we record catches up, and GP lands right.

     ⚠️ THE "NEVER DRAW THE SAME STEP TWICE" INVARIANT STILL HOLDS. It moved rather than lapsed: the
     Purchasing card now stands its freight-cost INPUT down while this card is on screen (see
     dashboard-po-lens.js) and shows the figure as a value instead. One number, one box, and the
     box is the one with the markup and charged fields beside it.

     ⚠️ Replacements come along too, via the same removal — a replacement is never "sold" but does
     incur a real freight cost we bear, and it had no cost surface here either. */
  function cardsApply(q) {
    if (!q) return false;
    if (!(getIsAdmin ? getIsAdmin() : false)) return false;
    if (q.source === 'online') return false;
    if (q.isPending || q.status === 'pending') return false;
    if (q.linkedParentRef) return false;
    return dealVendors(q).length > 0;
  }

  function dealVendors(q) {
    var v = (q && q.financials && q.financials.vendors) || [];
    return Array.isArray(v) ? v.filter(Boolean) : [];
  }

  /* Match a deal vendor ("ECORE") to its recorded freight row ("ECORE Athletic" canonicalised).
     ⚠️ Uses main's vendorNamesMatch, the same predicate the Purchasing card buckets POs with — a
     private lowercase compare here would disagree with the card three rows up the first time a
     vendor's quote name and its Guide name differed. */
  function freightRowFor(q, vendor) {
    var rows = (q.wsFreight && q.wsFreight.vendors) || [];
    for (var i = 0; i < rows.length; i++) {
      if (vendorNamesMatch ? vendorNamesMatch(rows[i].name, vendor)
                           : String(rows[i].name).toLowerCase() === String(vendor).toLowerCase()) {
        return rows[i];
      }
    }
    return null;
  }

  function blankRow(name) {
    return { name: name, cost: 0, carrier: '', type: 'unset', markup: 10, markedUpTotal: 0,
             notes: '', linkedTo: null, costEntered: false, chargedOverride: false, isSet: false };
  }

  /* Every vendor on the deal gets a row, whether or not freight has been recorded for it — an
     absent row is the case the card exists to serve. Any recorded row matching no deal vendor is
     appended rather than dropped, so a vendor removed from the quote after freight was quoted still
     shows its number instead of silently taking it off the deal's cost. */
  function rowsFor(q) {
    var out = dealVendors(q).map(function (v) {
      var r = freightRowFor(q, v);
      return r ? Object.assign({}, r, { _label: v }) : Object.assign(blankRow(v), { _label: v });
    });
    var recorded = (q.wsFreight && q.wsFreight.vendors) || [];
    recorded.forEach(function (r) {
      var claimed = out.some(function (o) {
        return vendorNamesMatch ? vendorNamesMatch(o.name, r.name)
                                : String(o.name).toLowerCase() === String(r.name).toLowerCase();
      });
      if (!claimed) out.push(Object.assign({}, r, { _label: r.name, _orphan: true }));
    });
    return out;
  }

  /* ⛔ ONE DERIVATION OF "WHAT DOES THIS CARD SAY", read by BOTH the renderer and the in-place
     patch further down. A quiet auto-save updates the header counter and the Apply button without
     rewriting the panel, so a patch that computed those a second way would drift from the render
     the first time either copy was edited. */
  /* ⛔ THREE COUNTS, NOT ONE, BECAUSE APPLY IS PARTIAL (Ryan 2026-08-29).

       setCount     — rows filled in well enough to APPLY. This is what the BUTTON counts:
                      "Apply (1 of 4)".
       appliedCount — rows actually ON the customer's quote. This is what the HEADER counts, and
                      Ryan asked for it explicitly: pressing Apply is what moves FREIGHT (0/4) to
                      FREIGHT (1/4), not filling a row in.
       pendingCount — set but not applied. The only thing that makes Apply clickable.

     ⚠️ `applied` is the SERVER'S per-row answer (ws-logistics vendorIsApplied), which is a
     signature compare, not a timestamp. So a row that was applied and then edited comes back
     unapplied on its own — which is the bug the old block-level `appliedAt` had: the card kept
     reading "Applied ✓" over an edited number that was not on the quote. */
  /* ⛔ A SUMMARY BAKED BEFORE PER-ROW STATE EXISTED CARRIES NO `applied` FIELD AT ALL, and the card
     must not read that absence as "nothing is on the quote". RW-1351-B is the case: freight and
     installation both applied from the Workstation weeks ago, every row set, and the card said
     (0/5) and (0/1) because its baked summary predates the field.

     ⚠️ THIS IS EVERY DEAL IN THE BOOK until the bulk re-bake runs, not one deal. The server has the
     same legacy read (ws-logistics vendorIsApplied) but it only runs at BAKE time, so it cannot
     reach a summary that was written before it shipped.

     ⛔ ONLY WHEN THE FIELD IS ABSENT. `applied === false` is a FRESH bake saying "genuinely not
     applied" and must never be overridden — that is the difference between healing stale data and
     inventing an answer. Read-only, and it retires itself the moment the deal re-bakes. */
  function rowApplied(r, blockAppliedAt, isSet) {
    if (!r) return false;
    if (r.applied !== undefined && r.applied !== null) return !!r.applied;
    return !!(blockAppliedAt && isSet);
  }

  function freightState(q) {
    var wf = q.wsFreight || {};
    var rows = rowsFor(q);
    var standalone = rows.filter(function (r) { return !r.linkedTo; });
    var setCount = standalone.filter(function (r) { return r.isSet; }).length;
    var appliedCount = standalone.filter(function (r) { return rowApplied(r, wf.appliedAt, r.isSet); }).length;
    var pendingCount = standalone.filter(function (r) { return r.isSet && !rowApplied(r, wf.appliedAt, r.isSet); }).length;
    /* ⚠️ Kept for the collapse rule and for records baked before per-row state existed. It is NOT
       what the button or the counter read any more. */
    var applied = !!wf.appliedAt;
    return {
      block: wf, rows: rows, standalone: standalone,
      setCount: setCount, totalCount: standalone.length,
      appliedCount: appliedCount, pendingCount: pendingCount,
      applied: applied,
      synced: q.freightSynced !== false,
      done: standalone.length > 0 && appliedCount === standalone.length,
      counter: counterFor(appliedCount, standalone.length),
    };
  }

  function installState(q) {
    var wi = q.wsInstallation || {};
    var companies = (wi.companies || []).slice(0, 2);
    var isSet = function (c) { return !!(c && c.type && c.type !== 'unset'); };
    var setCount = companies.filter(isSet).length;
    var appliedCount = companies.filter(function (c) { return rowApplied(c, wi.appliedAt, isSet(c)); }).length;
    var pendingCount = companies.filter(function (c) { return isSet(c) && !rowApplied(c, wi.appliedAt, isSet(c)); }).length;
    var applied = !!wi.appliedAt;
    return {
      block: wi, companies: companies,
      setCount: setCount, totalCount: companies.length,
      appliedCount: appliedCount, pendingCount: pendingCount,
      applied: applied,
      synced: q.installSynced !== false,
      done: companies.length > 0 && appliedCount === companies.length,
      counter: counterFor(appliedCount, companies.length),
    };
  }

  /* The header reads "FREIGHT (1/2)" — how many of this card's rows are actually ON the customer's
     quote, in the header's own type rather than a grey aside (Ryan 2026-08-29).
     ⚠️ Nothing when there is nothing to count: an installation card with no installer on it would
     otherwise say "(0/0)", which looks like a bug rather than an empty card. */
  function counterFor(appliedCount, totalCount) {
    return totalCount > 0 ? '(' + appliedCount + '/' + totalCount + ')' : '';
  }

  function cardState(kind, q) { return kind === 'freight' ? freightState(q) : installState(q); }

  /* ⛔ THE BUTTON COUNTS `setCount`, THE HEADER COUNTS `appliedCount`, AND THAT IS DELIBERATE.
     "Apply (1 of 4)" says what pressing it will put on the quote; "FREIGHT (1/4)" says what is
     already there. Reading one number for both would either light the button after an apply with
     nothing left to do, or leave the header claiming a vendor was quoted because somebody typed a
     cost.

     ⚠️ "Applied ✓" ONLY WHEN EVERY ROW IS ON THE QUOTE. Showing it after a partial apply would
     strand the other three suppliers behind a dead-looking control — which is exactly what the old
     all-or-nothing label did the moment partial apply landed. */
  function applyLabelFor(st) {
    var total = st.totalCount || 0;
    var pending = st.pendingCount || 0;
    var appliedCount = st.appliedCount || 0;
    if (pending > 0) {
      return { ready: true, label: 'Apply (' + st.setCount + ' of ' + total + ')' };
    }
    if (total > 0 && appliedCount === total) {
      return { ready: false, label: 'Applied ✓' };
    }
    if (appliedCount > 0) {
      return { ready: false, label: 'Applied (' + appliedCount + ' of ' + total + ')' };
    }
    return { ready: false, label: 'Apply (0 of ' + total + ')' };
  }

  /* ── the collapsed header ────────────────────────────────────────────────────────────────────
     Ryan: the cards must not disappear once filled — they collapse but stay readable, so costs and
     carrier are visible "at a glance without loading the whole order".

     ⛔ WHICH IS WHY THE CARD CARRIES data-section-id. applyCollapseState slugs a card's identity
     from its TITLE TEXT (sectionSlug reads titleEl.textContent), so putting live values in the
     title would change the slug every time a cost changed and reset the collapse state on each
     edit. The attribute pins the identity; the summary is then free to say anything. */
  function summaryHtml(text) {
    if (!text) return '';
    return '<span class="rqd-section-title__summary">' + esc(text) + '</span>';
  }

  function titleWithSummary(label, done, sectionId, text) {
    var base = sectionTitleHtml(label, done);
    var summary = summaryHtml(text);
    if (!summary) return base;
    /* Injected INSIDE the title div — the collapsed rule hides every child of the card except
       .rqd-detail__section-title, so anything outside it vanishes on collapse, which is the one
       state this text exists for.

       ⛔ AND BEFORE THE ✓, SO THE HEADER READS "FREIGHT (1/1) ✓" (Ryan 2026-08-29). Appending at
       `</div>` put the summary AFTER the check span, giving "FREIGHT ✓ (1/1)" — the tick wedged
       between the word and the bracket. The count belongs with the label it counts; the tick
       closes the whole thing.
       ⚠️ Done HERE, not in sectionTitleHtml — that is window.RQD.sectionTitleHtml, shared with
       every other detail card on the board, and moving the ✓ there would move it on all of them. */
    var CHECK = '<span class="rqd-detail__section-done"';
    var at = base.indexOf(CHECK);
    return at === -1
      ? base.replace('</div>', summary + '</div>')   // not done yet — no tick to sit behind
      : base.slice(0, at) + summary + base.slice(at);
  }

  // ── shared row bits ────────────────────────────────────────────────────────────────────────

  /* ⛔ THE RED PROMPTS ARE THE APPLY GATE'S OWN FAILING CLAUSES, NOT A SECOND OPINION.
     Ryan 2026-08-29: entering a cost should outline the `?` and the carrier red until they are
     filled, and Apply lights when they are. That gate is `freightVendorIsSet` in
     src/lib/ws-logistics.js — type set, cost entered, carrier non-empty — so the outline is
     rendered straight off the same three clauses. Writing a separate "is this row incomplete"
     rule here is how a red box ends up sitting next to a lit Apply button.

     ⚠️ ONLY ONCE A COST EXISTS. An untouched row is not "wrong", it is empty, and lighting six
     blank rows red on load says the card is broken rather than that it is waiting. An explicit
     $0 counts as a cost — some suppliers genuinely do not bill freight.

     ⚠️ Install has NO carrier clause. installApplyBlockers gates on the mark alone, so prompting
     for a carrier there would make the dashboard stricter than the Workstation and demand
     something before an apply the QW would already accept. */
  function rowPrompts(kind, r) {
    var costEntered = kind === 'freight'
      ? !!(r.costEntered || n2(r.cost) > 0)
      : !!(n2(r.cost) > 0);
    if (!costEntered || r.linkedTo) return { type: false, carrier: false };
    return {
      type: !r.type || r.type === 'unset',
      carrier: kind === 'freight' ? !String(r.carrier || '').trim() : false,
    };
  }

  function typeToggleHtml(kind, idx, type, needs) {
    var t = TYPE_GLYPH[type] ? type : 'unset';
    return '<button type="button" class="rqd-fqc__type rqd-fqc__type--' + t + (needs ? ' rqd-fqc__needs' : '') + '"'
      + ' data-fqc-action="cycle-type" data-fqc-kind="' + kind + '" data-fqc-idx="' + idx + '"'
      + ' data-fqc-type="' + t + '"'
      + ' title="' + esc(TYPE_TITLE[t]) + '" aria-label="Quote type: ' + t + '. ' + esc(TYPE_TITLE[t]) + '">'
      + TYPE_GLYPH[t] + '</button>';
  }

  /* CUSTOM_FIXEDCOST_BY_CLAUDE - the Fixed cost box: the row's fee (worked out or typed), blank when none is owed. */
  function fixedCostInputHtml(idx, fee) {
    var a = fee ? parseFloat(fee.amount) : NaN;
    var typed = !!(fee && fee.auto === false);
    var val = (isFinite(a) && (a > 0 || typed)) ? (Math.round(a * 100) / 100).toFixed(2) : '';
    var what = typed ? 'typed in' : (fee && fee.label ? fee.label + (fee.count ? ' (' + fee.count + ' x $' + (Math.round((parseFloat(fee.rate) || 0) * 100) / 100).toFixed(2) + ')' : '') : 'pallet / handling fees');
    return '<div class="rqd-fqc__fixed-wrap" title="Fixed cost: pallet / handling fees for the products on this quote (' + esc(what)
      + '). Added to the freight cost before the markup. Type a figure to replace it; empty the box to use the supplier fee again.">'
      + moneyInputHtml('rqd-fqc__fixed rqd-fqc__f', 'fixed', 'freight', idx, val, 'Fixed', 'Fixed cost: pallet / handling fees, added before the markup')
      + '</div>';
  }

  function moneyInputHtml(cls, field, kind, idx, value, placeholder, aria) {
    return '<div class="rqd-freight-cost-input-wrap">'
      + '<span class="rqd-freight-cost-prefix">$</span>'
      + '<input type="text" inputmode="decimal" class="rqd-freight-cost-input ' + cls + '"'
      + ' data-fqc-field="' + field + '" data-fqc-kind="' + kind + '" data-fqc-idx="' + idx + '"'
      + ' value="' + esc(value) + '" placeholder="' + esc(placeholder || '0.00') + '"'
      + ' aria-label="' + esc(aria) + '">'
      + '</div>';
  }

  /* The tickboxes.

     ⛔ THE BOX IS `.rqd-toggle__check` — THE CARD'S OWN STEP CHECKBOX, REUSED, NOT REDRAWN.
     Ryan 2026-08-29: *"make them be the same size and UI of the larger checkboxes for weight &
     dims, Cost, markup, etc."* That class already carries the 20x20 / 2px border / 6px radius and
     the green filled state, so borrowing it means these can never drift from the rows above them —
     which is the whole reason they looked wrong: a native 13px checkbox next to a 20px drawn one.

     The real <input> stays in the markup and stays the thing that is clicked and read by assistive
     tech; it is visually hidden and the span is painted from its :checked state. ⚠️ The `--on` class
     is written here as well as relying on `:has()`, because `:has()` is the only thing painting the
     ticked state otherwise and a browser without it would show every ticked box as unticked — a
     silent wrong answer on a control that changes a supplier email. */
  function checkboxHtml(action, key, label, checked, extra) {
    return '<label class="rqd-fqc__check' + (checked ? ' rqd-fqc__check--on' : '') + '">'
      + '<input type="checkbox" data-fqc-action="' + action + '" data-fqc-key="' + key + '"'
      + (checked ? ' checked' : '') + (extra || '') + '>'
      + '<span class="rqd-toggle__check" aria-hidden="true">&#10003;</span>'
      /* data-text is the SAME string as the visible label and is not decoration: the stylesheet
         renders it a second time, bold and hidden, to reserve the checked width. If the two ever
         diverge the reservation is the wrong size and the layout shift comes straight back — which
         is why a test asserts they match. */
      + '<span class="rqd-fqc__check-label" data-text="' + esc(label) + '">' + esc(label) + '</span>'
      + '</label>';
  }

  // ── FREIGHT QUOTE card ─────────────────────────────────────────────────────────────────────

  /* CUSTOM_FREIGHTREQSTALE_BY_CLAUDE (ONYX 918a2409ed5e, Ryan 2026-10-09 - JW-1310-A sat 51h, JW-1312-B 28h) - a rep's
     freight request nobody has acted on yet (no supplier ask, no cost) says so at the top of the card, with the
     working hours it has waited (Mon-Fri 7 AM - 7 PM Mountain, the rule the agent reminders use). The worker decides
     WHETHER (q.freightRequestWait, absent once anything is done); the card only counts the hours from `since`, so a
     board baked hours ago still shows the right number. Amber under 4 working hours, red from 4 (Onyx is re-woken). */
  var FRW_DTF = null;
  function frwParts(ms) {
    if (!FRW_DTF) FRW_DTF = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Denver', hour12: false, weekday: 'short', hour: '2-digit' });
    var o = {}; FRW_DTF.formatToParts(new Date(ms)).forEach(function (p) { o[p.type] = p.value; });
    return { wd: o.weekday, h: parseInt(o.hour, 10) % 24 };
  }
  function freightRequestWorkingHours(sinceIso, nowMs) {
    var from = Date.parse(sinceIso || ''); if (!isFinite(from) || nowMs <= from) return 0;
    /* Chunks cut on clock quarter hours: Denver is a whole number of hours from UTC, so each chunk is wholly
       inside or outside the 7-19 window and the count is exact (no rounding up at 7 PM). */
    var Q = 15 * 60000, ms = 0, cap = 4000, t = from;
    while (t < nowMs && cap-- > 0) {
      var next = Math.min(nowMs, (Math.floor(t / Q) + 1) * Q);
      var p = frwParts(t);
      if (p.wd !== 'Sat' && p.wd !== 'Sun' && p.h >= 7 && p.h < 19) ms += next - t;
      t = next;
    }
    return Math.round(ms / 360000) / 10; // one decimal
  }
  function freightRequestWaitHtml(q) {
    var w = q && q.freightRequestWait;
    if (!w || !w.since) return '';
    var hrs = freightRequestWorkingHours(w.since, Date.now());
    var when = formatDate(w.requestedAt || w.since) || '';
    var hot = hrs >= 4;
    var what = w.why === 'answered-no-cost' ? 'The supplier answered, no freight cost saved yet' : 'Freight requested' + (w.requestedBy ? ' by ' + esc(w.requestedBy) : '') + (when ? ' ' + esc(when) : '') + ', nothing done yet';
    return '<div class="rqd-fqc__wait" data-fqc-wait="1" style="margin:4px 0 8px;padding:6px 10px;border-radius:6px;font-size:12px;line-height:1.4;'
      + (hot ? 'color:#8a1c1c;background:#fde8e8;border:1px solid #f3b4b4' : 'color:#7a5a00;background:#fff4d6;border:1px solid #f0d58a') + '">'
      + what + ' &middot; ' + hrs + ' working hour' + (hrs === 1 ? '' : 's') + '</div>';
  }
  /* CUSTOM_DEALSTREET_BY_CLAUDE (ONYX e39b242081a1, Ryan 2026-10-12, RW-1376-D) - an offline deal that is sold or has
     freight requested, with no street on its delivery address (the worker's q.streetMissing). Freight must not be
     quoted or booked without it; the line says whether the deal owner has been asked. */
  function streetMissingHtml(q) {
    var m = q && q.streetMissing;
    if (!m || !m.missing) return '';
    var asked = m.askedAt ? ' The deal owner was asked' + (m.askedOwner ? ' (' + esc(m.askedOwner) + ')' : '') + ' on ' + esc(formatDate(m.askedAt) || m.askedAt) + '.' : '';
    return '<div class="rqd-fqc__street" data-fqc-street="1" style="margin:4px 0 8px;padding:6px 10px;border-radius:6px;font-size:12px;line-height:1.4;'
      + 'color:#f8b4b4;background:rgba(220,38,38,.14);border:1px solid rgba(220,38,38,.55)">'
      + '<strong>Street missing</strong>: no street address to deliver to' + (m.where ? ' (' + esc(m.where) + ')' : '') + '. Freight cannot be quoted or booked until it is on the deal.' + asked + '</div>';
  }
  function renderDealFreightCard(q) {
    if (!cardsApply(q)) return '';
    var ref = q.reference;
    var st = freightState(q);
    var wf = st.block, rows = st.rows, standalone = st.standalone;
    var setCount = st.setCount;
    /* ⛔ `appliedAt`, NOT `status === 'quoted'`. /freight-cost-save stamps the status 'quoted' the
       moment ANY vendor has a cost > 0 — it means "we have a number", not "it is on the customer's
       quote". Only the Apply path writes appliedAt. Reading the status here would show "Applied ✓"
       and auto-collapse the card as soon as a cost was typed, hiding the one control that had not
       been used yet. ⚠️ Latent until now and about to stop being: this card makes the cost-save
       path the common one. Measured across 83 live records — 46 applied, every one carrying
       appliedAt, none with the status alone — so reading appliedAt loses nothing historical. */
    var applied = st.applied;

    /* CUSTOM_FQCSETTLED_BY_CLAUDE (Ryan 2026-09-12) — ⛔ GHOSTED, NOT DISABLED, AND THE DIFFERENCE
       IS THE WHOLE POINT. Ryan asked for the freight inputs to read as settled once a deal is sold.
       Disabling them would have re-broken the exact thing the comment at the top of this file was
       written to fix: the carrier's CORRECTED rate arrives weeks AFTER the sale, and this card is
       the only surface carrying the cost + markup + "keep" trio that records it without moving what
       the customer was charged. So the class below changes how the card LOOKS and nothing about
       what it does — every field stays typeable, every button stays clickable.
       ⚠️ If a future session turns this into `disabled` or `pointer-events: none`, read that comment
       first: it will be taking the post-sale freight correction away with it. */
    var _settled = (q.lifecycleStatus === 'sold' || q.status === 'sold');
    var html = '<div class="rqd-detail__arap rqd-fqc' + (_settled ? ' rqd-fqc--settled' : '')
      + '" data-section-id="freight-quote" data-ref="' + esc(ref) + '">';
    /* ⚠️ Titled "Freight", not "freight-quote" — and the two are allowed to differ ONLY because the
       card declares data-section-id. Before that, renaming a heading silently moved its collapse
       identity. */
    html += titleWithSummary('Freight', st.done, 'freight-quote', st.counter);
    html += '<div class="rqd-detail__arap-row">';
    html += freightRequestWaitHtml(q); // CUSTOM_FREIGHTREQSTALE_BY_CLAUDE (ONYX 918a2409ed5e)
    html += streetMissingHtml(q); // CUSTOM_DEALSTREET_BY_CLAUDE (ONYX e39b242081a1)

    rows.forEach(function (r, i) {
      html += renderFreightVendorRows(q, r, i, rows);
    });

    /* Order-level, not per-vendor — one shipment's conditions describe the destination, not the
       supplier. Same shape as the Workstation's footer row (rep-quote-workstation.liquid:513). */
    /* The five conditions sit BELOW the per-vendor rows and hang off the right edge (Ryan
       2026-08-29), lining up under the controls they qualify rather than starting a new column on
       the left. They are order-level, not per-vendor — one shipment's conditions describe the
       DESTINATION, not the supplier — which is why they are here once instead of on every row. */
    var dc = wf.deliveryConditions || {};
    html += '<div class="rqd-toggle rqd-fqc__flags" data-step="delivery-conditions" data-state="'
      + (dc.loadingDock || dc.forkLift || dc.insideDelivery ? 'special' : 'standard') + '">'
      + '<div class="rqd-fqc__flags-set">'
      + DELIVERY_FLAGS.map(function (f) {
          return checkboxHtml('delivery-flag', f.key, f.label, !!dc[f.key]);
        }).join('')
      + '</div></div>';

    html += renderApplyRow('freight', q, st);
    html += '</div></div>';
    return html;
  }

  function renderFreightVendorRows(q, r, i, allRows) {
    var ref = q.reference;
    var vendor = r._label;
    var vAttr = ' data-ref="' + esc(ref) + '" data-vendor="' + esc(vendor) + '"';
    var idxAttr = ' data-fqc-kind="freight" data-fqc-idx="' + i + '"';
    var fq = (q.freightQuotes || {})[vendor] || (q.freightQuotes || {})[r.name] || {};
    var requested = !!fq.requestedAt;
    var skipped = !!fq.skippedAt;
    var kind = fq.kind === 'dims' ? 'dims' : 'freight';
    var kindLabel = kind === 'dims' ? 'Weight &amp; dims' : 'Freight quote';
    var html = '';

    /* ⛔ A FOLDED VENDOR DRAWS NOTHING OF ITS OWN. Ryan 2026-08-29: when Stairmaster ships with
       Nautilus, Stairmaster's rows go away and the NAUTILUS header becomes "NAUTILUS +
       STAIRMASTER", with a `split` beside it. A folded supplier has no cost, no carrier and no ask
       of its own — every one of those questions is answered by the shipment it travels on — so the
       three rows it used to draw were three rows of nothing.

       ⚠️ IT IS STILL IN `allRows` AND STILL HOLDS ITS INDEX. Skipping the RENDER rather than
       filtering the array is deliberate: `data-fqc-idx` indexes rowsFor(q), which saveFreightRow
       and the set-linked handler both re-read by index. Filtering here would silently shift every
       index after the fold and save one vendor's numbers onto another. */
    if (r.linkedTo) return '';

    /* Who travels on this shipment. */
    var folded = allRows.filter(function (o) { return o.linkedTo && o.linkedTo === r.name; });
    var standaloneCount = allRows.filter(function (o) { return !o.linkedTo; }).length;

    /* One thin rule + a name between vendor groups. Multi-vendor freight puts several .rqd-toggle
       rows per supplier into one flat flex column, and without a seam two suppliers' rows read as
       a single long list — the same problem the Purchasing card solved.

       ⚠️ THE GATE COUNTS FOLDS TOO. It used to be "more than one row"; with folded rows no longer
       drawing, a two-vendor deal with one folded would fall to a single visible row and lose its
       header — taking "NAUTILUS + STAIRMASTER" with it, which is now the ONLY place the
       combination is stated. */
    if (standaloneCount > 1 || folded.length) {
      html += '<div class="rqd-fqc__vendor-head"' + (i > 0 ? ' data-sep="1"' : '') + '>'
        + esc(vendor)
        + folded.map(function (o) { return ' + ' + esc(o._label || o.name); }).join('')
        + (r._orphan ? ' <span class="rqd-fqc__orphan" title="Freight was recorded for this supplier, but it is no longer on the deal’s vendor list.">not on this deal</span>' : '')
        /* ⛔ SPLIT IS THE ONLY WAY BACK. The folded vendor's own "Ships with" select is gone with
           its rows, so without this control a fold would be permanent. */
        + (folded.length
            ? '<button type="button" class="rqd-fqc__split" data-fqc-action="split"'
              + ' data-ref="' + esc(ref) + '" data-fqc-idx="' + i + '"'
              + ' title="' + esc('Give ' + folded.map(function (o) { return o._label || o.name; }).join(', ')
                  + ' its own shipment again') + '">split</button>'
            : '')
        + '</div>';
    }

    /* ── A. OPTIONS — the ask, the shipment it travels on, and skip, on ONE row ───────────────
       Ryan 2026-08-29: *"we can combine Freight quote and Ships with onto 1 line."* Three rows per
       supplier become two. Options sits ABOVE Breakdown because that is the order the work happens
       in — contact the supplier, then type in what they told you.

       ⛔ REUSES DashboardHandshake with bucket OMITTED. That is the legacy path, which stamps
       lifecycle.freightQuotes[vendor].{requestedAt,requestedBy,kind,scheduledFor} — the same record
       the Workstation's own ✉ buttons write, and the same one the truck icon and the Purchasing
       card read. Buckets are an ONLINE construct (deriveShippingBucketsForQuote reads
       onlineLineItems); passing one here would stamp a bucket that does not exist. */
    var askedFreight = !!fq.requestedAt;
    var askedDims = !!(fq.dimsRequestedAt || (fq.requestedAt && fq.kind === 'dims'));
    /* ⛔ THE STAMP NAMES WHICH ASK WENT OUT, because the row label no longer can. It used to read
       "Freight quote" or "Weight & dims"; now it reads "Options" for both, so the kind has to live
       in the stamp (Ryan 2026-08-29: *"record next to it something like - requested dims 8/29"*).
       ⚠️ AND THIS IS WHY 'both' IS NOT A THIRD `kind` VALUE. requestedAt is ONE timestamp — a third
       enum would overwrite freight-Monday with dims-Wednesday and the row could only ever name one
       of them. Two stamps can say both. */
    var stamps = [];
    if (askedFreight && fq.kind !== 'dims') stamps.push('freight ' + (formatDate(fq.requestedAt) || ''));
    if (askedDims) stamps.push('dims ' + (formatDate(fq.dimsRequestedAt || fq.requestedAt) || ''));
    var requested = askedFreight || askedDims;

    if (skipped) {
      html += '<div class="rqd-toggle rqd-toggle--done"' + vAttr + ' data-step="freight-quote" data-state="skipped">'
        + '<div class="rqd-toggle__check" style="cursor:default;">&#10003;</div>'
        + '<span>Options</span>'
        + '<span class="rqd-toggle__date">skipped' + (fq.skippedAt ? ' ' + esc(formatDate(fq.skippedAt) || '') : '') + '</span>'
        + '<div class="rqd-invoice">'
        + '<button type="button" class="rqd-freight-reset-link" data-freight-action="unskip"' + vAttr
        + ' title="Ask ' + esc(vendor) + ' for freight after all">undo</button>'
        + '</div></div>';
    } else {
      /* CUSTOM_FQCSETTLED_BY_CLAUDE (Ryan 2026-09-12) — on a SOLD deal the Options row is asking
         whether to go and get a freight quote, which is a question that has already been answered
         by the sale. It takes the house "n/a" treatment: the same green `--done` row every other
         settled step uses, with `n/a` in the check slot rather than a tick, because a tick would
         claim we asked this vendor when we may well not have.
         ⚠️ The requested/skipped stamps below still render — the row says the question is moot, not
         that the history is gone. Lowercase 'n/a' and `data-state="not-applicable"` are the
         conventions the AR/AP card set (dashboard-ar-ap-card.js). */
      var _naSold = (q.lifecycleStatus === 'sold' || q.status === 'sold');
      var _naTitle = 'This deal has sold, so there is nothing left to ask the vendor for here.';
      html += '<div class="rqd-toggle' + (requested || _naSold ? ' rqd-toggle--done' : '') + '"' + vAttr
        + ' data-step="' + (fq.kind === 'dims' ? 'weight-dims' : 'freight-quote') + '"'
        + ' data-state="' + (_naSold ? 'not-applicable' : (requested ? 'requested' : 'not-requested')) + '"'
        + (_naSold ? ' title="' + _naTitle + '" data-blocked-reason="' + _naTitle + '"' : '') + '>'
        + '<div class="rqd-toggle__check' + (_naSold ? ' rqd-toggle__check--na' : '') + '" style="cursor:default;">'
        + (_naSold ? 'n/a' : (requested ? '&#10003;' : '')) + '</div>'
        + '<span>Options</span>';
      if (stamps.length) {
        html += '<span class="rqd-toggle__date">requested ' + esc(stamps.join(' · ')) + '</span>';
        /* A HELD email is not a sent one, and the only thing that ever said so was the absence of a
           stamp. Reuses the shared builder so this row gets the same ⏳ pill, with its aria-label,
           as the four rows that already carry it. */
        if (fq.scheduledFor && window.RQD.scheduledReleaseBtnHtml) {
          /* ⚠️ TWO DIFFERENT WORDS FOR TWO DIFFERENT THINGS in that builder's signature: `kind`
             picks the PO-vs-freight branch, `type` is freight-vs-dims. */
          html += window.RQD.scheduledReleaseBtnHtml({
            kind: 'freight', ref: ref, vendor: vendor, type: (fq.kind === 'dims' ? 'dims' : 'freight'),
            bucket: '', scheduledFor: fq.scheduledFor,
          });
        }
      }
      html += '<div class="rqd-invoice">';
      /* ⛔ ONE BLUE BUTTON, NOT A SELECT PLUS A BUTTON (Ryan 2026-08-29). The old pairing made you
         set a dropdown you could not see the effect of, then press a button; this asks the question
         once, at the moment you press it.
         `rqd-fqc-ask` IS THE HOOK FOR THE ROW'S STATUS COLOUR, not decoration — dashboard.css
         declares a `--rqd-lf-bg` / `--rqd-lf-fg` accent pair for every deal status and this class
         joins the selector list that reads it, the same way the Samples card's buttons do. */
      html += '<div class="rqd-fqc__menu-wrap">'
        + '<button type="button" class="rqd-invoice__link rqd-fqc-ask' + (requested ? ' rqd-invoice__generate' : '') + '"'
        + ' data-fqc-action="contact-menu"' + vAttr + idxAttr
        + ' aria-haspopup="true" aria-expanded="false"'
        + ' title="' + esc(requested ? 'Ask ' + vendor + ' again' : 'Email ' + vendor) + '">'
        + (requested ? 'Contact again' : 'Contact vendor') + '</button>'
        + '<div class="rqd-fqc__menu" hidden>'
        + '<button type="button" class="rqd-fqc__menu-item" data-fqc-action="ask" data-fqc-ask="freight"' + vAttr + idxAttr + '>Freight quote</button>'
        + '<button type="button" class="rqd-fqc__menu-item" data-fqc-action="ask" data-fqc-ask="dims"' + vAttr + idxAttr + '>Weight &amp; dims</button>'
        + '<button type="button" class="rqd-fqc__menu-item" data-fqc-action="ask" data-fqc-ask="both"' + vAttr + idxAttr + '>Both</button>'
        + '</div></div>';
      html += shipsWithControl(q, r, i, allRows);
      /* ⛔ SKIP ONLY WHERE GENERATE PO CAN ACTUALLY BLOCK. All `skip` does is un-grey a disabled
         Generate PO — and that button only greys for a vendor whose Vendor Guide workflow is
         `freight-then-po`. MEASURED 2026-08-29: two of eight vendor profiles qualify (ECORE
         Athletic, Spirit Fitness), and across all 469 live lifecycle records `skippedAt` appears
         ZERO times. So on six suppliers in eight it was a control that could not do anything,
         which is exactly why Ryan could not work out what it was for.

         📌 The SAME predicate the progress bar, the To Do strip and the PO gate already read, so
         the four cannot drift apart — that unification is what the 2026-08-17 fix was for.
         ⚠️ Read at CALL time, not captured at load: these modules load BEFORE dashboard-main.js.
         Absent predicate → no button, which is the safe direction for a control nobody has ever
         used and which cannot help unless the gate it clears is live. */
      var _RQD = window.RQD || {};
      var _owesFreight = typeof _RQD.vendorNeedsFreightHandshake === 'function'
        && _RQD.vendorNeedsFreightHandshake(q, vendor);
      if (!requested && _owesFreight) {
        html += '<button type="button" class="rqd-freight-reset-link" data-freight-action="skip"' + vAttr
          + ' title="Send the PO without waiting for this supplier’s number. Records a decision; it does NOT record $0.">skip</button>';
      }
      html += '</div></div>';
    }

    /* ── B. BREAKDOWN — cost, type, markup, charged, carrier, notes, save, on ONE line ────────
       Ryan 2026-08-29: cost / ? / % / charged / carrier / notes all belong on the row that used to
       be just "Cost", so the whole supplier quote reads left to right in one pass instead of three
       stacked rows saying one thing each.

       ⛔ THE ROW SAVES ITSELF ON LEAVING IT, AND APPLY IS THE COMMIT. There is no save button any
       more (Ryan 2026-08-29). What answers "did that save?" — the complaint that put a floppy here
       in the first place ("it's not clear if you need to press enter, click outside the box, just
       sit there and wait") — is now the red outline clearing and Apply going live as you fill the
       row in. Still never blur-save PER FIELD: rerenderDetail blurs the active element by design,
       cost is required by the endpoint, and the endpoint writes a deal-feed note per cost change.

       📌 WIDTHS COME FROM THE SHARED BUTTON FORMULA, not from numbers typed here.
       `.rqd-invoice__link` is `calc(140px * var(--rqd-sold-input-scale, 1))`, and the Samples card
       sets that scale to 0.8 — which is the width Ryan pointed at. Reusing the formula means these
       fields track the standard button if it is ever resized, rather than drifting off it.

       ⚠️ `data-fqc-last` records which of markup / charged was touched last, because they are two
       ends of one relationship: a typed CHARGED figure back-solves the markup, a typed MARKUP
       recomputes charged. The save sends only the one that was edited — sending both would make the
       server choose, and it would choose differently from the Workstation. */
    var costStr = (r.costEntered || n2(r.cost) > 0) ? fixed2(r.cost) : '';
    var pr = rowPrompts('freight', r);
    /* ⛔ THE TICK MEANS "ON THE CUSTOMER'S QUOTE", NOT "FILLED IN" (Ryan 2026-08-29). Pressing
       Apply is what ticks it. It used to read `isSet`, so a row ticked itself the moment a carrier
       was typed and the card claimed a supplier was quoted before anybody had applied anything. */
    var rApplied = rowApplied(r, (q.wsFreight || {}).appliedAt, r.isSet);
    /* CUSTOM_SUPPLIERFEE_BY_CLAUDE (ONYX 41775fe3a0bb) - the supplier's pallet / handling fee rides on the row; the live
       charged preview prices on cost + fee, the same base the worker uses. */
    var _rowFee = (r.supplierFee && parseFloat(r.supplierFee.amount) > 0) ? Math.round(parseFloat(r.supplierFee.amount) * 100) / 100 : 0;
    html += '<div class="rqd-toggle rqd-fqc__breakdown' + (rApplied ? ' rqd-toggle--done' : '') + '"' + vAttr
      + ' data-step="freight-cost" data-state="' + (rApplied ? 'applied' : (r.isSet ? 'ready' : 'not-recorded')) + '"'
      + ' data-fqc-fee="' + _rowFee + '"'
      + ' data-fqc-last="' + (r.chargedOverride ? 'charged' : 'markup') + '">'
      + '<div class="rqd-toggle__check" style="cursor:default;">' + (rApplied ? '&#10003;' : '') + '</div>'
      + '<span>Breakdown</span>'
      + '<div class="rqd-freight-cost-wrap rqd-fqc__wrap">'
      /* CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-08-30) — ↺ HOLD, ported from the Quote
         Workstation. Ryan: *"when I do type in a new number into the cost input field after it's
         already had a number in there which has been saved, the 'Keep' button which you get in the
         QW isn't appearing… a good place for it will be to the left of the cost input field. Same
         size/shape button as the cost input field."*

         ⛔ IT IS THE ANSWER TO THE ONE THING A COST EDIT BREAKS. Typing a new cost live-recomputes
         charged from the markup (`recalcRow`), which is right while quoting and wrong once the
         customer has been given a number: the carrier's corrected rate must move OUR cost and GP
         without moving what they were billed. Hold puts the pre-edit charged figure back and lets
         the markup absorb the difference.

         ⚠️ IT WRITES NOTHING NEW. It sets the charged box and marks the row `fqcLast='charged'`,
         which is the path that already exists: the save then posts `charged`, and the server
         back-solves the markup and sets `_chargedOverride` — the same field the QW's Hold sets. No
         second endpoint, no second rule about what a held charge means.

         ⛔ AND IT LIVES INSIDE `.rqd-fqc__breakdown` ON PURPOSE. `handleFocusOut` skips a blur
         whose `relatedTarget` is still in the same row, so a button in here cannot trigger a save
         mid-click — which would race the value it is about to set. Outside the row it would.

         ⚠️ `hidden` AND a display rule. `.rqd-freight-cost-wrap` is a flex container, so a child
         with an explicit `display` would beat the `hidden` attribute — the trap this codebase has
         hit before. dashboard.css pairs this class with `[hidden]{display:none!important}`. */
      + '<button type="button" class="rqd-fqc__hold" data-fqc-action="hold" data-fqc-kind="freight"'
      +   ' data-fqc-idx="' + i + '" hidden></button>'
      + moneyInputHtml('rqd-fqc__cost rqd-fqc__f', 'cost', 'freight', i, costStr, '0.00', 'Freight cost from ' + vendor)
      /* CUSTOM_FIXEDCOST_BY_CLAUDE (Ryan 2026-10-16, RW-1513-A) - the "Fixed cost" box between the carrier cost and the
         markup: the supplier's pallet / handling fees for the products on the quote (filled in by the worker), or a figure
         typed over it. price = (cost + fixed cost) x (1 + markup). Emptying the box goes back to the worked-out fee. */
      + fixedCostInputHtml(i, r.supplierFee)
      + '<div class="rqd-fqc__pct-wrap rqd-fqc__h">'
      + '<input type="text" inputmode="decimal" class="rqd-fqc__markup" data-fqc-field="markup" data-fqc-kind="freight" data-fqc-idx="' + i + '"'
      + ' value="' + esc(String(r.markup == null ? 10 : r.markup)) + '" aria-label="Freight markup percent">'
      + '<span class="rqd-fqc__pct">%</span></div>'
      + moneyInputHtml('rqd-fqc__charged rqd-fqc__f', 'charged', 'freight', i, fixed2(r.markedUpTotal), '0.00', 'Freight charged to the customer')
      /* ⚠️ THE ?/E/✓ SITS AFTER THE CHARGED FIGURE, NOT AFTER THE COST (Ryan 2026-08-29). It marks
         the whole supplier quote estimate-or-official, so reading it at the END of cost → markup →
         charged says "and this is how firm all of that is", where sitting mid-formula it read as a
         property of the cost alone. */
      + typeToggleHtml('freight', i, r.type, pr.type)
      /* ⚠️ A DATALIST INPUT, NOT A <select>, and that is deliberate. It carries the Workstation's
         carrier presets so it drops down like one, but the Workstation lets an admin type a carrier
         that is not on the list — a real select would silently make that impossible, which is a
         regression dressed as a tidier control. Styled to the same box as its neighbours. */
      + '<input type="text" class="rqd-fqc__carrier rqd-fqc__w' + (pr.carrier ? ' rqd-fqc__needs' : '') + '" data-fqc-field="carrier" data-fqc-kind="freight" data-fqc-idx="' + i + '"'
      + ' value="' + esc(r.carrier || '') + '" placeholder="Carrier"'
      /* ⛔ NO `list=` ANY MORE. A native datalist cannot inline-complete, so pairing it with the
         custom suggestions would put two dropdowns on one box. The presets did not go anywhere —
         suggestionsFor('carrier') merges them in behind the names actually used. */
      + ' autocomplete="off" aria-label="Carrier moving this shipment">'
      /* CUSTOM_PARCELLANE_BY_CLAUDE (ONYX e1baa9591ccf, Ryan 2026-10-09) - a cost with no carrier reads as a TRUCK on the
         Order Desk, the price check and the PO (a FedEx / UPS / USPS carrier puts the supplier on the parcel lane).
         A person may still save it blank (Ryan's pick); this says what blank means. Agents are refused a blank carrier. */
      + (pr.carrier ? '<span class="rqd-fqc__carrier-note" style="font-size:11px;line-height:1.3;color:#7a5a00;background:#fff4d6;border:1px solid #f0d58a;border-radius:4px;padding:1px 6px;white-space:nowrap;align-self:center">Carrier unknown: treated as truck</span>' : '')
      + '<input type="text" class="rqd-fqc__notes rqd-fqc__w" data-fqc-field="notes" data-fqc-kind="freight" data-fqc-idx="' + i + '"'
      + ' value="' + esc(r.notes || '') + '" placeholder="Notes"'
      + ' aria-label="Freight notes for ' + esc(vendor) + '">'
      + '</div></div>';

    return html;
  }

  /* "Ships with" is how the Workstation expresses one truck carrying two suppliers' goods — a FOLD,
     not a second shipment. The folded vendor keeps its row but contributes 0 to both totals and its
     items ride in the primary's request email. Only offered when there is another standalone vendor
     to fold into, and never on a vendor that is already somebody's primary. */
  /* The shipment picker, now a control INSIDE the Options row rather than a row of its own
     (Ryan 2026-08-29). Offered only where it can do something: a vendor that other suppliers are
     already folded INTO cannot itself be folded, and with nothing to fold into there is no choice
     to make.

     ⛔ THE PLACEHOLDER IS A PROMPT NOW ("— Ships with —"), NOT A STATE ("Its own shipment"), AND
     THAT IS ONLY SAFE BECAUSE SPLIT EXISTS. The empty option used to do two jobs: it was the
     resting label AND the thing you picked to unfold. Renaming it to a prompt while it still meant
     "unfold" would have asked people to select an instruction in order to undo something. Now that
     a folded vendor draws no row at all and unfolding happens on the header's `split`, this option
     is only ever the resting state and never a choice anybody makes.

     📌 A DATALIST-STYLE dash label rather than a disabled option: the dashes read as "not a value",
     the same convention the Date filter's separators use. */
  function shipsWithControl(q, r, i, allRows) {
    var isPrimary = allRows.some(function (o) { return o.linkedTo && o.linkedTo === r.name; });
    /* ⛔ A COMBINED SHIPMENT KEEPS A CONTROL — IT JUST MEANS THE OPPOSITE THING. Ryan 2026-08-29:
       *"the Ships with button shouldn't disappear when 2 vendors have already been combined —
       should still be able to link those 2 with a 3rd or 4th."*

       Folding a 3rd vendor in was always possible, but only from that vendor's OWN row, and the
       one row nobody looks at for it is the row that already says NAUTILUS + STAIRMASTER. So the
       primary now offers "Add to shipment", which folds the CHOSEN vendor into this one — the
       inverse direction of the same relationship, on the row where the group is described.

       ⛔ AND THE PRIMARY MUST NOT BE OFFERED THE NORMAL CONTROL. `linkedTo` is one level deep:
       recalcFreightTotals zeroes a row that has one, and nothing resolves a chain. Letting
       Nautilus ship with ECORE while Stairmaster ships with Nautilus would leave Stairmaster
       pointing at a row that itself contributes nothing, and the totals would quietly under-count.
       Which is why candidates on BOTH branches exclude vendors that are themselves primaries. */
    var isPrimaryOf = function (o) {
      return allRows.some(function (x) { return x.linkedTo && x.linkedTo === o.name; });
    };
    if (isPrimary) {
      var addable = allRows.filter(function (o) {
        return o._label !== r._label && !o.linkedTo && !isPrimaryOf(o);
      });
      if (!addable.length) return '';
      return '<select class="rqd-fqc__linked" data-fqc-action="add-to-shipment" data-fqc-kind="freight"'
        + ' data-fqc-idx="' + i + '" data-empty="1"'
        + ' aria-label="Add another supplier’s goods to this shipment">'
        + '<option value="">Add to shipment</option>'
        + addable.map(function (o) {
            return '<option value="' + esc(o.name) + '">' + esc(o._label) + '</option>';
          }).join('')
        + '</select>';
    }
    /* ⚠️ A PRIMARY IS A VALID TARGET AND MUST STAY IN THIS LIST. The chain risk is one-directional:
       a primary becoming a FOLLOWER strands the rows already pointing at it. Joining an existing
       combined shipment is the ordinary case — excluding primaries here (which the first draft of
       this did) quietly removed "Nautilus" from every other vendor's list the moment Stairmaster
       folded into it, leaving the group joinable from exactly one row. */
    var candidates = allRows.filter(function (o) {
      return o._label !== r._label && !o.linkedTo;
    });
    if (!candidates.length) return '';
    return '<select class="rqd-fqc__linked" data-fqc-action="set-linked" data-fqc-kind="freight" data-fqc-idx="' + i + '"'
      + (r.linkedTo ? '' : ' data-empty="1"')
      + ' aria-label="Fold this supplier’s goods into another shipment">'
      /* ⛔ `data-empty` IS WHAT LETS THE CLOSED CONTROL BE GREYED. A `<select>` shows the SELECTED
         option's text when closed, and an `option[value=""]` rule paints the item in the open
         list only — the resting control would still read at full strength. The attribute puts
         the state on the element CSS can actually reach. */
      + '<option value="">Ships with</option>'
      + candidates.map(function (o) {
          return '<option value="' + esc(o.name) + '"' + (r.linkedTo === o.name ? ' selected' : '') + '>' + esc(o._label) + '</option>';
        }).join('')
      + '</select>';
  }

  // ── INSTALLATION QUOTE card ────────────────────────────────────────────────────────────────

  /* ⛔ TITLED "Installation Quote", NOT "Installation". applyCollapseState slugs from the title, and
     `installation` is already claimed by the SCHEDULING card (dashboard-main.js) and by its own
     shouldAutoCollapse branch. Two cards on one slug share collapse state and one branch decides
     for both. `installation-quote` is free. */
  function renderDealInstallCard(q) {
    if (!cardsApply(q)) return '';
    var ref = q.reference;
    var st = installState(q);
    var wi = st.block, companies = st.companies;
    var applied = st.applied;

    /* CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-09-01) - ⛔ A SOLD DEAL WITH NO INSTALLATION HAS NO
       INSTALLATION CARD. `cardsApply` asks whether this deal is the KIND that gets these cards
       (admin, offline, not pending, has vendors); it never asked whether installation is RELEVANT.
       So every sold offline deal carried a card whose only content was an offer to add an
       installer to an order that had already shipped. Ryan, on RW-1339-A: *"since this deal is
       sold with no installation, the entire card should disappear."*

       ⚠️ SOLD IS THE GATE, not merely "empty". Before the sale this card is where an installer GETS
       added, so an unsold deal keeps it even when empty. And a sold deal that HAS installation
       keeps it too - admins need to see the cost at a glance long after the fact.

       ⚠️ AND THERE IS STILL A ROUTE IN AFTERWARDS, which is the only reason hiding it is safe.
       Ryan: *"we add install via the QW at that point. or more likely, we create a new separate
       deal just for installation."*

       The emptiness test is deliberately wider than `companies.length`: a requested-but-unquoted
       install, an applied total, or a quoted figure each mean this card has something to say. */
    /* CUSTOM_DDINSTALL_BY_CLAUDE (Ryan 2026-09-03) — ⛔ AND THE CARD GOES WHEN THE PROPOSAL SAYS
       THERE IS NO INSTALL, sold or not. Ryan: *"if the deal owner marks the install as 'None',
       which is what they'll do when the customer doesn't intend to purchase an install from us,
       the 'INSTALLATION' card on the DD should stop appearing at all."*

       ⛔ `installFeeStatus` IS THE FEES DROPDOWN, AND IT IS NOT `wsInstallation.status`. Those are
       two different fields and only one of them is what the deal owner touches. The QW's
       Installation row (Pending / $ / FREE / None) writes `state.fees[1].status` -> `wm.rawFees[1]`;
       `wm.installation.status` is the LOGISTICS block and the Workstation only ever writes
       'requested' or 'quoted' to it — nothing anywhere sets it to 'none'. So the 2026-05-19
       `_installRemoved` defence in quote-summary-builder.js keys off a value the dropdown never
       produces. This reads the field the human actually sets.

       ⚠️ ABSENT MEANS SHOW. `installFeeStatus` is new, so every summary baked before it shipped
       carries nothing — and reading that absence as 'none' would delete the card from deals that
       genuinely have installers on them. It corrects itself as deals re-bake.

       ⛔ ONLY WHEN THERE IS NOTHING ON THE CARD TO STRAND. Installer paid, the schedule and the
       'Install completed' control all live here now, and the worker's CLOSED gate needs
       `install.status === 'complete'` — so hiding a card that still holds real install state would
       make the deal unclosable and an unpaid installer invisible. `_noInstall` is the same
       emptiness test the sold rule uses, widened below.

       ⚠️ FREE AND $0.00 DELIBERATELY KEEP THE CARD, which is narrower than the rule as written
       ("anything other than PENDING or a dollar amount >$0"). A FREE install is one we are still
       performing — it needs an installer, a cost, a schedule and a payment — and a `$` row sitting
       at 0.00 is an unfinished entry rather than a decision. `none` is the only value that says
       the customer is not buying an install. Flagged to Ryan; narrow it here if he wants it. */
    var _soldLike = (q.lifecycleStatus === 'sold' || q.status === 'sold');
    var _feeNone = q.installFeeStatus === 'none';

    /* ⛔ A ROW WITH NOTHING IN IT IS NOT AN INSTALLER. `!companies.length` was the old test, and it
       counted the blank rows the Workstation used to plant on every admin load (see
       workstation-freight.js) — so a deal whose only "installer" was that phantom kept a card the
       sold rule was written to remove, reading INSTALLATION (0/1) over an empty row. */
    var _rowHasData = function (c) {
      return !!(c && ((c.type && c.type !== 'unset')
        || String(c.name || '').trim()
        || parseFloat(c.cost) > 0
        || parseFloat(c.markedUpTotal) > 0
        || String(c.notes || '').trim()));
    };
    var _inst = q.install || {};
    var _noInstall = !companies.some(_rowHasData)
      && wi.status !== 'requested'
      && !wi.appliedAt
      && !(parseFloat(wi.totalQuoted) > 0)
      && !(parseFloat(wi.totalCost) > 0)
      /* The fulfilment half. A scheduled, completed or paid install must keep its card even when
         every other signal reads empty — those controls exist nowhere else. */
      && !(_inst.scheduledDate && String(_inst.scheduledDate).trim())
      && _inst.status !== 'complete'
      && !q.installerPaid;
    if ((_soldLike || _feeNone) && _noInstall) return '';

    var html = '<div class="rqd-detail__arap rqd-fqc rqd-fqc--install" data-section-id="installation-quote" data-ref="' + esc(ref) + '">';
    html += titleWithSummary('Installation', st.done, 'installation-quote', st.counter);
    html += '<div class="rqd-detail__arap-row">';

    if (wi.status === 'requested' && !companies.length) {
      html += '<div class="rqd-toggle" data-step="install-request" data-state="requested">'
        + '<div class="rqd-toggle__check" style="cursor:default;">&#10003;</div>'
        + '<span>Installation requested by ' + esc(wi.requestedBy || 'the rep') + '</span>'
        + (wi.requestedAt ? '<span class="rqd-toggle__date">' + esc(formatDate(wi.requestedAt) || '') + '</span>' : '')
        + '</div>';
    }

    /* ⛔ NO EMPTY-STATE ROW. Ryan 2026-08-29: *"if there's no installation added, don't include the
       line with the checkbox that just says 'No installer on this quote'."* The "Add an installer"
       row below is the empty state — a tickbox-shaped row saying nothing happened, directly above a
       row offering to make something happen, was two rows telling one story.
       ⚠️ The `requested` case above still draws: "Installation requested by the rep" is a fact about
       somebody's action, not an absence, and it was ALSO drawing a second row underneath saying the
       quote had not come back yet. That pairing is gone with this. */
    companies.forEach(function (c, i) { html += renderInstallCompanyRows(q, c, i); });

    if (companies.length < MAX_INSTALLERS) {
      html += '<div class="rqd-toggle rqd-fqc__add-row">'
        + '<div class="rqd-toggle__check" style="cursor:default;"></div>'
        + '<span>' + (companies.length ? 'Second installer' : 'Add an installer') + '</span>'
        + '<div class="rqd-invoice">'
        /* ⛔ `rqd-fqc-ask` IS THE PER-STATUS ACCENT HOOK, not decoration — the same class Contact
           vendor carries, and the same pair the Samples card's Adjust / Request buttons read.
           dashboard.css declares a `--rqd-lf-bg` / `--rqd-lf-fg` accent for every deal status, and
           this class joins the selector list that picks it up. `rqd-fb-btn--primary` was a
           one-off that ignored the panel's status colour entirely (Ryan 2026-08-29: it should be
           "the light blue UI button, like Contact again or Adjust or Request"). */
        + '<button type="button" class="rqd-invoice__link rqd-fqc-ask" data-fqc-action="add-installer"'
        + ' data-ref="' + esc(ref) + '" data-fqc-idx="' + companies.length + '"'
        + ' title="The Workstation holds up to two installers per quote">+ Add installer</button>'
        + '</div></div>';
    }

    /* ⛔ MUTUAL EXCLUSION IS STRUCTURAL, NOT POLICED — scope is ONE string, so ticking one box
       necessarily clears the other and there is no "uncheck the sibling" branch to get wrong.
       Unticking the active box sends ''. Same single writer as Q.setInstallScope in the QW. */
    html += '<div class="rqd-toggle rqd-fqc__flags" data-step="install-scope" data-state="' + (wi.scope || 'full') + '">'
      + '<div class="rqd-fqc__flags-set">'
      /* ⛔ "WHOLE JOB" IS A REAL BOX NOW, NOT AN ITALIC ASIDE (Ryan 2026-08-29). Three boxes for
         three states of ONE field, so it behaves like a radio group wearing checkboxes: `scope` is
         a single string, and '' IS whole-job. That is why ticking Flooring or Equipment unchecks
         Whole job on its own — there is no second field to contradict, and no "uncheck the sibling"
         branch that could get it wrong.
         ⚠️ It is checked by DEFAULT because a new installer row has no scope, which is exactly
         what '' means. And it cannot be unticked directly: there is no fourth state to untick INTO,
         so clicking it while checked is a no-op and clicking either other box is how it turns off. */
      + checkboxHtml('install-scope', 'whole', 'Whole job', !wi.scope)
      + checkboxHtml('install-scope', 'flooring', 'Flooring only', wi.scope === 'flooring')
      + checkboxHtml('install-scope', 'equipment', 'Equipment only', wi.scope === 'equipment')
      + '</div></div>';

    html += renderApplyRow('install', q, st);
    html += renderInstallFulfilmentRows(q);
    html += '</div></div>';
    return html;
  }

  /* CUSTOM_DDINSTALL_BY_CLAUDE (Ryan 2026-09-02) — THE FULFILMENT HALF OF THE INSTALLATION CARD.

     ⛔ INSTALL STATE WAS SPREAD ACROSS THREE CARDS. This one held the installer and the cost; a
     SECOND card further down the panel (dashboard-main.js, also titled "Installation") held the
     scheduled-date picker; and Installer Paid sat on the AR/AP card. Ryan, on RW-1068-B: *"we now
     have 2x installation cards... we need to combine the 2nd one where you select the date onto
     the first one."* All three now render here, in the order the work actually happens:
     quoted -> scheduled -> completed -> paid.

     ⛔ AND NOTHING ANYWHERE COULD MARK A JOB INSTALLED. The old card's own comment admitted it:
     *"the card is 'done' once a date is scheduled (the terminal state this card can reach — there's
     no 'mark complete' control)"*. Meanwhile the worker's CLOSED gate has always required
     `install.status === 'complete'`, so six deals sat open on a state no screen could reach.
     `handleInstallSave` has read `.rqd-install-form__status` since it was written; the control it
     was reading never existed. This is that control.

     📌 Ryan chose the explicit row over inferring it from payment: *"there should be a simple row
     on the INSTALLATION (1/1) card that says 'Install completed' then on the drop-down I can select
     yes or leave it unselected which means no."* Inferring installation from payment would be the
     wrong direction — a deposit to an installer is not a finished job.

     ⚠️ INSTALLER PAID IS GATED ON COMPLETED, which is the sequence Ryan described (*"then when
     that's done, that ungates the opportunity to select when the installer was paid"*) and the
     order the progress bar already implies. `opts.disabled` omits `data-field`, so the row cannot
     be clicked and its date input renders greyed — visible-but-locked, so an admin can see what is
     coming rather than wondering where the control went.

     ⚠️ AND ITS DATE IS GENUINELY OPTIONAL — `optionalDate`. Every other closeout toggle stamps
     `todayStr()` the instant you tick it, so a payment recorded a week late has always been filed
     under the wrong day. Ryan: *"in the hustle and bustle of the job, it's easy to forget to mark
     these things on the exact day, so sometimes it's best to not make something up and just say
     'yes we paid them but I don't remember the exact date'."* Ticking the row records paid with NO
     date; picking a date in the inline picker records both. */
  function renderInstallFulfilmentRows(q) {
    var _sold = (q.lifecycleStatus === 'sold' || q.status === 'sold');
    var _cost = parseFloat((q.financials || {}).installCost || 0);
    if (!_sold || !(_cost > 0)) return '';

    var ref = q.reference;
    var inst = q.install || {};
    var admin = !!(getIsAdmin && getIsAdmin());
    var scheduled = !!(inst.scheduledDate && String(inst.scheduledDate).trim());
    var completed = inst.status === 'complete';
    var html = '';

    /* ⛔ EACH ROW CARRIES ITS OWN `.rqd-vendor-tracking.rqd-install-form`, because the delegated
       change listener in dashboard-main.js finds the row with `closest('.rqd-vendor-tracking')` and
       then asks whether THAT element is an install form — a shared outer wrapper would fail that
       test and route the save to the tracking handler instead. `handleInstallSave` looks the two
       controls up across the whole card, not within one row, for the same reason. */
    html += '<div class="rqd-vendor-item">'
      + '<div class="rqd-vendors__check' + (scheduled ? ' rqd-vendors__check--done' : '') + '" style="pointer-events:none;">'
      + '<span style="font-size:12px;">' + (scheduled ? '&#10003;' : '') + '</span></div>'
      + '<span class="rqd-vendor-item__name">Scheduled</span>'
      + '<div class="rqd-vendor-tracking rqd-install-form" data-ref="' + esc(ref) + '">'
      + '<span class="rqd-vendor-tracking__status"></span>';
    /* Installs are scheduled by admins, who coordinate installer, rep and customer on a date. Reps
       see it read-only — the worker enforces the same rule, so this is display, not security. */
    if (admin) {
      html += '<input type="date" class="rqd-vendor-tracking__input rqd-install-form__date'
        + (scheduled ? ' rqd-install-form__date--filled' : '') + '" value="' + esc(inst.scheduledDate || '') + '">';
    } else {
      html += '<span class="rqd-vendor-tracking__input rqd-install-form__date rqd-install-form__date--readonly" aria-readonly="true">'
        + (scheduled ? esc(inst.scheduledDate) : 'Admin will schedule') + '</span>';
    }
    html += '</div></div>';

    html += '<div class="rqd-vendor-item">'
      + '<div class="rqd-vendors__check' + (completed ? ' rqd-vendors__check--done' : '') + '" style="pointer-events:none;">'
      + '<span style="font-size:12px;">' + (completed ? '&#10003;' : '') + '</span></div>'
      + '<span class="rqd-vendor-item__name">Install completed</span>'
      + '<div class="rqd-vendor-tracking rqd-install-form" data-ref="' + esc(ref) + '">'
      + '<span class="rqd-vendor-tracking__status"></span>';
    if (admin) {
      /* ⚠️ UNSELECTED MEANS NO, and it is the default — Ryan asked for exactly two options rather
         than a Yes/No pair, so an untouched deal never asserts that the job did NOT happen. */
      /* CUSTOM_DDINSTALL_BY_CLAUDE (Ryan 2026-09-02) — the chosen option reads "Completed", and the
         box ghosts once it is. Ryan: *"center the text in that box and make it 'Completed', and
         then when it's marked as completed, ghost/gray out the box so it's clear it's been done."*

         📌 The SAME two-state grammar the date input beside it already uses — awaiting input is
         solid, done is a transparent ghost outline (`rqd-install-form__date--filled`). A row whose
         two controls disagreed about what "done" looks like would read as two different rows.

         ⚠️ GHOSTED, NOT DISABLED. The select stays interactive so the status can be taken back to
         "-- Select --" (which `handleInstallSave` writes as `pending`); greying it out for real
         would make a wrong tick unfixable from the card. */
      html += '<select class="rqd-vendor-tracking__input rqd-install-form__status'
        + (completed ? ' rqd-install-form__status--done' : '') + '">'
        + '<option value="">-- Select --</option>'
        + '<option value="complete"' + (completed ? ' selected' : '') + '>Completed</option>'
        + '</select>';
    } else {
      html += '<span class="rqd-vendor-tracking__input rqd-install-form__date rqd-install-form__date--readonly" aria-readonly="true">'
        + (completed ? 'Completed' : 'Not yet') + '</span>';
    }
    html += '</div></div>';

    if (admin && buildToggle) {
      html += buildToggle(ref, 'installerPaid', 'Installer paid', q.installerPaid, q.installerPaidDate, {
        editableDate: true,
        optionalDate: true,
        disabled: !completed,
        disabledReason: 'Mark the install completed first',
      });
    }
    return html;
  }

  function renderInstallCompanyRows(q, c, i) {
    var ref = q.reference;
    var vAttr = ' data-ref="' + esc(ref) + '"';
    var isSet = !!c.type && c.type !== 'unset';
    var pr = rowPrompts('install', c);
    var costStr = n2(c.cost) > 0 ? fixed2(c.cost) : '';
    var html = '';

    /* ⛔ THE SAME COLUMN ORDER AS THE FREIGHT ROW, POSITION FOR POSITION (Ryan 2026-08-29):
       cost · % · charged · ? · name · notes. The installer NAME sits where the CARRIER sits, which
       is what makes the two cards read as one layout when they are stacked on the same panel — and
       it is why the Apply row's column totals land under the right boxes on both.
       ⚠️ The remove `×` leads the row and has no counterpart on freight, which has nothing to
       remove: a vendor comes off the deal, not off this card. */
    var cApplied = rowApplied(c, (q.wsInstallation || {}).appliedAt, isSet);
    html += '<div class="rqd-toggle rqd-fqc__breakdown' + (cApplied ? ' rqd-toggle--done' : '') + '"' + vAttr
      + ' data-step="install-cost" data-state="' + (cApplied ? 'applied' : (isSet ? 'ready' : 'not-recorded')) + '"'
      + ' data-fqc-last="' + (c.chargedOverride ? 'charged' : 'markup') + '">'
      + '<div class="rqd-toggle__check" style="cursor:default;">' + (cApplied ? '&#10003;' : '') + '</div>'
      + '<span>Breakdown</span>'
      + '<div class="rqd-freight-cost-wrap rqd-fqc__wrap">'
      /* ⛔ FIRST IN THE MARKUP, NOT NUDGED LEFT WITH flex `order` (Ryan 2026-08-29: far left of the
         left-most control). Ordering it visually while leaving it last in the DOM would make the
         tab order disagree with what is on screen. It IS destructive and it IS now the first thing
         you land on tabbing into the row, which is only acceptable because it already goes through
         a confirm ("Remove this installer? Its cost comes off the deal.").
         ⛔ ITS OWN CLASS. `.rqd-freight-reset-link` is shared by eleven controls across three
         modules (skip, unskip, cancel, reset, Void, Revert); restyling it here would drag all of
         them along. This is an icon button, not a text link. */
      + '<button type="button" class="rqd-fqc__remove" data-fqc-action="remove-installer"'
      + ' data-ref="' + esc(ref) + '" data-fqc-idx="' + i + '"'
      + ' title="Remove this installer" aria-label="Remove this installer">&times;</button>'
      /* The install twin. Same relationship, same trap: `recalcInstallTotals` re-derives the
         charged figure from the markup unless `_chargedOverride` is set, so a corrected installer
         invoice would move what the customer was billed. See the note on the freight row above. */
      + '<button type="button" class="rqd-fqc__hold" data-fqc-action="hold" data-fqc-kind="install"'
      +   ' data-fqc-idx="' + i + '" hidden></button>'
      + moneyInputHtml('rqd-fqc__cost rqd-fqc__f', 'cost', 'install', i, costStr, '0.00', 'Installation cost')
      + '<div class="rqd-fqc__pct-wrap rqd-fqc__h">'
      + '<input type="text" inputmode="decimal" class="rqd-fqc__markup" data-fqc-field="markup" data-fqc-kind="install" data-fqc-idx="' + i + '"'
      + ' value="' + esc(String(c.markup == null ? 10 : c.markup)) + '" aria-label="Installation markup percent">'
      + '<span class="rqd-fqc__pct">%</span></div>'
      + moneyInputHtml('rqd-fqc__charged rqd-fqc__f', 'charged', 'install', i, fixed2(c.markedUpTotal), '0.00', 'Installation charged to the customer')
      + typeToggleHtml('install', i, c.type, pr.type)
      + '<input type="text" class="rqd-fqc__carrier rqd-fqc__w" data-fqc-field="name" data-fqc-kind="install" data-fqc-idx="' + i + '"'
      + ' value="' + esc(c.name || '') + '" placeholder="Company" aria-label="Installer company name">'
      + '<input type="text" class="rqd-fqc__notes rqd-fqc__w" data-fqc-field="notes" data-fqc-kind="install" data-fqc-idx="' + i + '"'
      + ' value="' + esc(c.notes || '') + '" placeholder="Notes" aria-label="Installation notes">'
      + '</div></div>';

    return html;
  }

  /* ── Apply to Quote ─────────────────────────────────────────────────────────────────────────
     ⛔ THIS IS THE ONE CONTROL HERE THAT CHANGES WHAT THE CUSTOMER IS BILLED. It writes the fee row
     and the Shopify draft line, exactly as the Workstation's Apply does, which is why it states its
     own gate rather than just going grey: "Apply (1 of 2 set)" tells the admin what is missing;
     a disabled button with no reason sends them to the Workstation to find out, which is the trip
     this card exists to save.

     ⚠️ The gate is the WORKSTATION'S (freightVendorIsSet — type AND carrier on every standalone
     vendor; install — estimate/official on every company), enforced again server-side. A card that
     offered Apply where the QW refuses it would be the two surfaces disagreeing about the same
     record on the same deal. */
  /* The three column figures under the Breakdown inputs.

     ⛔ THEY COVER THE ROWS THAT WILL ACTUALLY GO ON THE QUOTE, not every row on screen. This is
     the APPLY row: a charged total here that did not match what Apply sends would be a wrong
     number in the one place somebody reads before pressing a button that bills a customer. A row
     with a cost typed and no carrier yet is excluded, which is the same rule appliedFreightTotals
     uses on the server.

     ⚠️ `pct` is BLENDED (charged ÷ cost), not the mean of the per-row percentages — see the note
     at the call site. Empty rather than "0%" when there is no cost to divide by, because a 0%
     printed between two blank totals reads as a real markup of zero. */
  function applyRowTotals(kind, st, fallbackCharged) {
    var rows = kind === 'freight'
      ? (st.standalone || []).filter(function (r) { return r.isSet; })
      : (st.companies || []).filter(function (c) { return c && c.type && c.type !== 'unset'; });
    var cost = 0, charged = 0;
    rows.forEach(function (r) {
      cost += n2(r.cost);
      charged += n2(r.markedUpTotal);
    });
    cost = n2(cost);
    charged = n2(charged);
    /* A summary baked before appliedTotalQuoted existed has no per-row figures to re-sum here
       either, so fall back to the block total rather than showing $0 next to a real quote. */
    if (!rows.length && fallbackCharged) charged = fallbackCharged;
    var pct = '';
    if (cost > 0) pct = String(Math.round(((charged / cost) - 1) * 1000) / 10) + '%';
    else if (charged > 0) pct = '—';
    return { cost: cost, charged: charged, pct: pct };
  }

  function renderApplyRow(kind, q, st) {
    var block = st.block;
    var _al = applyLabelFor(st);
    var ready = _al.ready, label = _al.label;
    /* ⛔ THE APPLIED SUM, NOT THE FULL ONE. `totalQuoted` adds up every row including the ones
       still missing a carrier; `appliedTotalQuoted` is the sum over rows that are SET, which is
       both what Apply will write and what is on the quote once it has. On a record baked before
       per-row state existed the field is absent, so fall back rather than render $0. */
    var quoted = n2(block.appliedTotalQuoted != null ? block.appliedTotalQuoted : block.totalQuoted);
    var totals = applyRowTotals(kind, st, quoted);
    /* ⛔ "WILL BE" UNTIL THE CUSTOMER HAS ACTUALLY SEEN IT (Ryan 2026-08-29). Three states, not
       two: applied-but-the-sent-PDF-predates-it is still "will be", because the number is on the
       quote document and not yet in the customer's hands. `synced` is the builder's
       freightSynced / installSynced (lastPdfSentAt vs freightLastModifiedAt).
       ⚠️ GUARDED ON appliedCount. `synced` is vacuously true when nothing has ever been recorded,
       so a blank card would otherwise announce that the customer has been quoted $0.
       ⚠️ And lastPdfSentAt stamps on every PDF DOWNLOAD, so an admin generating the PDF to check
       it themselves flips this. It is a strong proxy, not proof. */
    var hasBeenQuoted = st.appliedCount > 0 && st.pendingCount === 0 && st.synced;
    var lede = hasBeenQuoted ? 'Customer has been quoted' : 'Customer will be quoted';
    var why = kind === 'freight'
      ? 'A supplier needs a cost, a carrier and an estimate/official mark before it can go on the quote.'
      : 'An installer needs an estimate/official mark before it can go on the quote.';
    var doneRow = st.done;
    return '<div class="rqd-toggle rqd-fqc__apply-row' + (doneRow ? ' rqd-toggle--done' : '') + '"'
      + ' data-ref="' + esc(q.reference) + '" data-step="' + kind + '-apply"'
      + ' data-state="' + (doneRow ? 'applied' : (ready ? 'ready' : 'incomplete')) + '">'
      + '<div class="rqd-toggle__check" style="cursor:default;">' + (doneRow ? '&#10003;' : '') + '</div>'
      + '<span>' + lede + '</span>'
      /* ⛔ THE COLUMN TOTALS SIT IN THE SAME FLEX WRAP AS THE BREAKDOWN ROW, WITH THE SAME WIDTHS
         IN THE SAME ORDER, INCLUDING THE EMPTY ONES. The wrap is `justify-content: flex-end`, so
         the columns line up only if the trailing widths match exactly — drop the two spacers under
         the mark and the carrier and every total slides right by their combined width and reads
         as a total of the wrong column (Ryan 2026-08-29: totals aligned UNDER the inputs). */
      + '<div class="rqd-freight-cost-wrap rqd-fqc__wrap rqd-fqc__totals">'
      /* ⛔ A ZERO TOTAL RENDERS AS NOTHING, BUT THE SPAN STAYS (Ryan 2026-08-29: "I'd prefer if
         the $0.00 just got hidden... and these numbers only appeared when >0"). An empty card
         printing $0.00 twice reads as a priced quote of nothing. ⚠️ Blank the TEXT, never drop
         the element — the wrap above is justify-content:flex-end and lines these totals up under
         their inputs purely by trailing width, so removing a span slides every figure right and
         parks it under the wrong column. Same reason the two spacers below are kept.
         (`totals.pct` already self-blanks — applyRowTotals returns '' when there is nothing.) */
      + '<span class="rqd-fqc__total rqd-fqc__total--money rqd-fqc__f" title="Total cost across the rows that are ready to apply">'
      + esc(totals.cost > 0 ? money(totals.cost) : '') + '</span>'
      /* ⚠️ THE BLENDED MARKUP, NOT THE MEAN OF THE PERCENTAGES. It sits between the two totals it
         is derived from, so it has to reconcile with them: on RW-1351-B the per-row marks are
         5/0/5/10/0, whose mean is 4% — while the money actually moves 6,565 → 6,813, which is
         3.8%. Printing 4% between those two figures would look like an arithmetic error in the
         one place somebody checks the maths. */
      + '<span class="rqd-fqc__total rqd-fqc__total--pct rqd-fqc__h" title="Blended markup across those rows (charged ÷ cost), not the average of the per-row percentages">'
      + esc(totals.pct) + '</span>'
      + '<span class="rqd-fqc__total rqd-fqc__total--money rqd-fqc__f" title="Total charged to the customer">'
      + esc(totals.charged > 0 ? money(totals.charged) : '') + '</span>'
      + '<span class="rqd-fqc__spacer rqd-fqc__spacer--mark" aria-hidden="true"></span>'
      + '<span class="rqd-fqc__spacer rqd-fqc__spacer--fill" aria-hidden="true"></span>'
      /* Apply wears the row's status colour too — see the note on the ask button. It keeps the
         disabled treatment when the gate is not met, so "on-brand" never reads as "ready". */
      + '<button type="button" class="rqd-invoice__link rqd-fqc-apply rqd-fqc__f' + (ready ? '' : ' rqd-invoice__generate') + '"'
      + ' data-fqc-action="apply" data-fqc-kind="' + kind + '" data-ref="' + esc(q.reference) + '"'
      + (ready ? '' : ' disabled')
      + ' title="' + esc(ready ? 'Put ' + money(totals.charged) + ' on the customer’s quote and tell the rep' : why) + '"'
      + ' aria-label="' + esc(label + (ready ? '' : ' — ' + why)) + '">'
      + esc(label) + '</button>'
      + '</div></div>';
  }

  // ── Actions ────────────────────────────────────────────────────────────────────────────────

  function quoteFor(ref) {
    var all = getAllQuotes ? getAllQuotes() : [];
    return (all || []).find(function (x) { return x.reference === ref; }) || null;
  }

  /* Read one row's boxes. Scoped to the card the button is in — two expanded deals put two sets of
     [data-fqc-idx="0"] in the document, and a document-wide lookup would edit whichever painted
     first. Same reason the bucket handlers scope to .rqd-freight-cost-wrap. */
  function fieldEl(card, kind, idx, field) {
    return card.querySelector('[data-fqc-kind="' + kind + '"][data-fqc-idx="' + idx + '"][data-fqc-field="' + field + '"]');
  }
  function fieldVal(card, kind, idx, field) {
    var el = fieldEl(card, kind, idx, field);
    return el ? String(el.value || '').trim() : null;
  }
  function moneyVal(card, kind, idx, field) {
    var raw = fieldVal(card, kind, idx, field);
    if (raw == null || raw === '') return null;
    var v = parseFloat(raw.replace(/[$,\s]/g, ''));
    return isFinite(v) && v >= 0 ? Math.round(v * 100) / 100 : null;
  }

  function rowEl(card, kind, idx) {
    var el = fieldEl(card, kind, idx, 'markup');
    /* ⚠️ The markup / charged memo lives on the BREAKDOWN row now — the markup and notes rows were
       folded into it (Ryan 2026-08-29). A selector left pointing at the old row would find nothing,
       `rowEl` would return null, and every save would silently fall through to the markup branch,
       discarding a charged figure the admin had just typed. */
    return el ? el.closest('.rqd-fqc__breakdown') : null;
  }

  function busy(btn, on) {
    if (!btn) return;
    btn.disabled = !!on;
    btn.classList.toggle('rqd-invoice__link--loading', !!on);
  }

  /* ⛔ PATCHES ONLY WHAT THE SERVER JUST TOLD US. On an offline deal the server sets
     wm.financials.freightCost = wm.freight.totalCost, so the resolved figure IS the returned total
     — unless an our-carrier override exists, which the builder and reassertOurCarrierFreight both
     treat as the last word. Mirrored here rather than re-summing freightQuotes, which is a
     different set (it excludes nothing, where the totals exclude folded vendors).

     ⚠️ DELIBERATELY DOES NOT RECOMPUTE gpOfGoods, unlike the neighbouring applyFreightCost in
     dashboard-handshake.js. That one does `direct − cogs − freightCost`, while the builder computes
     gpOfGoods as `(directTotal + feeDiscounts) − costTotal` with no freight term at all — freight
     margin is addOnProfit, a separate line. Copying a formula that disagrees with the server would
     flash a wrong number and then correct itself, which is worse than the half-second the quiet
     refetch takes to bring the right one. Flagged for Ryan; not changed here. */
  function patchFreightFinancials(q, ws) {
    if (!q || !ws) return;
    q.wsFreight = ws;
    if (!q.financials) return;
    var fo = q.freightOverrides || {};
    var ourCarrier = Object.keys(fo).some(function (k) {
      var o = fo[k];
      return o && o.mode === 'our-carrier' && parseFloat(o.cost) > 0;
    });
    if (!ourCarrier) q.financials.freightCost = n2(ws.totalCost);
  }
  function patchInstallFinancials(q, ws) {
    if (!q || !ws) return;
    q.wsInstallation = ws;
    if (!q.financials) return;
    q.financials.installCost = n2(ws.totalCost);
  }

  function afterMutate(ref, q) {
    var detailEl = document.getElementById('rqd-detail-' + ref);
    if (detailEl && rerenderDetail) rerenderDetail(detailEl, q);
    if (refreshProgressBar) refreshProgressBar(ref);
  }

  /* ⛔ THE OUTPUT FIELD REACTS WHILE YOU TYPE (Ryan 2026-08-29): *"if I type in $500 into the
     input button and the % markup is set to 10%, then the output field should immediately change to
     $550... as soon as I start deleting characters in the $500 field the $550 should react right
     away and go down to $0 too."*

     Runs on `input`, so it fires per keystroke rather than on blur — the whole point is that a
     half-deleted cost does not sit next to a charged figure computed from the old one.

     ⚠️ AN UNPARSEABLE BOX IS ZERO, NOT "LEAVE IT". Mid-delete the field reads "", "5", "$"; treating
     those as "no change" is what leaves $550 showing under an empty cost, which is precisely the
     stale pairing Ryan is describing.

     ⚠️ NEVER WRITES THE FIELD BEING TYPED IN. Driving from cost or markup writes charged; driving
     from charged writes markup. Writing back into the focused box would fight the caret.

     📌 DISPLAY ONLY — nothing is saved here; leaving the row does that, and Apply is what puts it
     on the customer's quote. It does update data-fqc-last, so the save afterwards sends whichever
     of markup / charged was actually driven, exactly as the Workstation infers it. */
  /* CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-08-30) — show ↺ Hold once the charged figure has
     actually moved, and label it with the number it will restore.

     ⚠️ THE TEST IS THE CHARGED FIGURE, NOT THE COST. A cost edit that lands back on the same
     number — typing 840, deleting it, typing 840 again — has changed nothing the customer sees, and
     a Hold button offering to restore what is already there reads as a bug. Mirrors the QW's
     `syncHoldBtn`, including its 0.005 tolerance so float noise cannot surface it.

     ⚠️ Toggled, never re-rendered. Rebuilding the row would yank focus out of the cost box
     mid-typing, which is the note the QW's own version carries. */
  function syncHoldBtn(card, kind, idx) {
    if (!card) return;
    var btn = card.querySelector('.rqd-fqc__hold[data-fqc-kind="' + kind + '"][data-fqc-idx="' + idx + '"]');
    if (!btn) return;
    var row = btn.closest('.rqd-fqc__breakdown');
    var target = row ? parseFloat(row.dataset.fqcHold) : NaN;
    var chEl = fieldEl(card, kind, idx, 'charged');
    var now = chEl ? parseFloat(String(chEl.value || '').replace(/[$,\s]/g, '')) : NaN;
    var show = isFinite(target) && target > 0 && isFinite(now) && Math.abs(target - now) > 0.005;
    if (show) {
      btn.textContent = '↺ ' + (money ? money(target) : ('$' + target.toFixed(2)));
      btn.title = 'Put the charged amount back to ' + (money ? money(target) : ('$' + target.toFixed(2)))
        + ' and let the markup absorb the new cost — the customer keeps the number they were given.';
      btn.hidden = false;
    } else {
      btn.hidden = true;
    }
  }

  function recalcRow(card, kind, idx, driver) {
    var costEl = fieldEl(card, kind, idx, 'cost');
    var mkEl = fieldEl(card, kind, idx, 'markup');
    var chEl = fieldEl(card, kind, idx, 'charged');
    if (!costEl || !mkEl || !chEl) return;
    var num = function (el, strip) {
      var v = parseFloat(String(el.value || '').replace(strip, ''));
      return isFinite(v) ? v : 0;
    };
    var cost = num(costEl, /[$,\s]/g);
    var row = card.querySelector('[data-fqc-kind="' + kind + '"][data-fqc-idx="' + idx + '"]');
    row = row ? row.closest('.rqd-fqc__breakdown') : null;
    /* CUSTOM_SUPPLIERFEE_BY_CLAUDE - price = (cost + the supplier's fee) x markup, once a cost is typed.
       CUSTOM_FIXEDCOST_BY_CLAUDE - the Fixed cost box, when the row has one, is the fee. */
    var _fixedEl = fieldEl(card, kind, idx, 'fixed');
    var _fee = _fixedEl ? num(_fixedEl, /[$,\s]/g) : (row ? (parseFloat(row.dataset.fqcFee) || 0) : 0);
    var base = cost > 0 ? Math.round((cost + _fee) * 100) / 100 : cost;
    if (driver === 'charged') {
      var charged = num(chEl, /[$,\s]/g);
      mkEl.value = base > 0
        ? String(Math.round(((charged / base) - 1) * 100 * 10000) / 10000)
        : '0';
    } else {
      var markup = num(mkEl, /[%\s]/g);
      chEl.value = (Math.round(base * (1 + markup / 100) * 100) / 100).toFixed(2);
    }
    if (row) row.dataset.fqcLast = (driver === 'charged') ? 'charged' : 'markup';

    /* CLEARING THE COST CLEARS THE ESTIMATE/OFFICIAL MARK (Ryan 2026-08-29). An "E" or a check
       beside an empty cost is the row claiming a supplier quote is firm when it no longer holds
       one.

       EMPTY IS NOT THE SAME AS TYPED ZERO, and the difference is load-bearing: some suppliers
       genuinely bill nothing for freight, and an explicit $0 with a carrier is a real quote the
       Workstation's Apply gate accepts -- that is what `_costEntered` exists for. So this fires on
       an UNPARSEABLE box ("", "$", mid-delete) and leaves a deliberate 0 alone. */
    var rawCost = String(costEl.value || '').replace(/[$,\s]/g, '');
    if (rawCost === '' || !isFinite(parseFloat(rawCost))) {
      var tBtn = card.querySelector('[data-fqc-action="cycle-type"][data-fqc-kind="' + kind + '"][data-fqc-idx="' + idx + '"]');
      if (tBtn && tBtn.dataset.fqcType !== 'unset') {
        tBtn.dataset.fqcType = 'unset';
        tBtn.textContent = TYPE_GLYPH.unset;
        tBtn.className = 'rqd-fqc__type rqd-fqc__type--unset';
        tBtn.title = TYPE_TITLE.unset;
      }
    }
  }

  /* Entry point for main's delegated `input` listener. Synchronous and cheap — no request, no
     re-render, so it can run on every keystroke. */
  /* ── carrier + installer memory, and the inline preview ──────────────────────────────────────
     Ryan 2026-08-29: clicking the box should offer names we have used before, and typing "Fed"
     should preview "Ex" so Tab or the right arrow takes it.

     ⛔ THE SAME localStorage KEYS THE WORKSTATION USES, deliberately. `rq_ws_carrier_history` /
     `rq_ws_installer_history` are read and written by workstation-freight.js, so a name typed in
     either surface is offered by the other. ⚠️ AND THAT MEANS IT IS PER BROWSER, NOT SHARED: a
     fresh machine starts empty and another admin never sees your list. Matching the QW is what was
     asked for; a server-derived list (every installation.companies[].name across records) is the
     version that is actually right and is logged as follow-up rather than half-built here.

     ⚠️ EVERY ACCESS IS WRAPPED. localStorage throws outright in some privacy modes rather than
     returning null, and an exception here would take the whole row render down with it. */
  var AUTO_KEYS = { carrier: 'rq_ws_carrier_history', installer: 'rq_ws_installer_history' };
  function autoHistory(key) {
    try {
      var raw = window.localStorage.getItem(AUTO_KEYS[key]);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (v) { return typeof v === 'string' && v.trim(); }) : [];
    } catch (_) { return []; }
  }
  function rememberName(key, value) {
    var v = String(value || '').trim();
    if (!v) return;
    try {
      var list = autoHistory(key).filter(function (o) { return o.toLowerCase() !== v.toLowerCase(); });
      list.unshift(v);
      window.localStorage.setItem(AUTO_KEYS[key], JSON.stringify(list.slice(0, 20)));
    } catch (_) { /* private mode — the box still works, it just will not remember */ }
  }
  /* Carrier suggestions merge the presets in behind whatever has actually been used. */
  function suggestionsFor(key) {
    var hist = autoHistory(key);
    if (key !== 'carrier') return hist;
    var seen = {};
    hist.forEach(function (h) { seen[h.toLowerCase()] = true; });
    return hist.concat(CARRIER_PRESETS.filter(function (c) { return !seen[c.toLowerCase()]; }));
  }
  function suggestKeyFor(input) {
    var f = input && input.dataset && input.dataset.fqcField;
    if (f === 'carrier') return 'carrier';
    if (f === 'name' && input.dataset.fqcKind === 'install') return 'installer';
    return null;
  }

  /* ⛔ ONLY ON A UNIQUE PREFIX MATCH. On RW-1351-B both "Ferus" and "FedEx" are in play, so typing
     "Fe" must sit still and wait rather than guess — a preview that picks one of two is worse than
     none, because it is silently wrong half the time and you accept it by tabbing.
     ⛔ AND NEVER WHILE DELETING. `inputType` starting with "delete" is the guard: without it,
     backspacing over the "d" in "Fed" instantly puts it back and the field cannot be cleared at
     all. That is the bug that makes people hate this feature. */
  function inlineComplete(input, evt) {
    if (!input || !input.setSelectionRange) return;
    var it = evt && evt.inputType ? String(evt.inputType) : '';
    if (it.indexOf('delete') === 0) return;
    var key = suggestKeyFor(input);
    if (!key) return;
    var typed = String(input.value || '');
    if (!typed || input.selectionStart !== typed.length) return;   // not at the end — do not fight the caret
    var lower = typed.toLowerCase();
    var hits = suggestionsFor(key).filter(function (o) { return o.toLowerCase().indexOf(lower) === 0; });
    var exact = hits.filter(function (o) { return o.toLowerCase() !== lower; });
    if (exact.length !== 1) return;
    input.value = typed + exact[0].slice(typed.length);
    try { input.setSelectionRange(typed.length, input.value.length); } catch (_) {}
  }

  /* The suggestion list. Reuses the contact menu's look so the card has one dropdown, not two. */
  function showSuggestions(input) {
    hideSuggestions();
    var key = suggestKeyFor(input);
    if (!key || !document.createElement) return;
    var typed = String(input.value || '').trim().toLowerCase();
    var list = suggestionsFor(key).filter(function (o) {
      return !typed || o.toLowerCase().indexOf(typed) !== -1;
    }).slice(0, 8);
    if (!list.length) return;
    var wrap = input.parentNode;
    if (!wrap) return;
    /* ⚠️ THE CARRIER BOX'S PARENT IS THE FLEX ROW ITSELF, not a wrapper of its own like the money
       inputs have. So the menu is anchored by measurement rather than by `right: 0`, which would
       park it under the last control in the row instead of under the box it belongs to. */
    if (wrap.style && !wrap.style.position) wrap.style.position = 'relative';
    var menu = document.createElement('div');
    menu.className = 'rqd-fqc__menu rqd-fqc__suggest';
    menu.style.left = (input.offsetLeft || 0) + 'px';
    menu.style.top = ((input.offsetTop || 0) + (input.offsetHeight || 28) + 4) + 'px';
    menu.style.right = 'auto';
    list.forEach(function (name) {
      var item = document.createElement('button');
      item.type = 'button';
      item.className = 'rqd-fqc__menu-item';
      item.textContent = name;
      /* mousedown, not click: focusout fires first on a click and would tear the menu down before
         the selection landed. */
      item.addEventListener('mousedown', function (e) {
        e.preventDefault();
        input.value = name;
        hideSuggestions();
        input.focus();
        if (window.RQD.handleFreightCardInput) window.RQD.handleFreightCardInput(input);
      });
      menu.appendChild(item);
    });
    wrap.appendChild(menu);
  }
  function hideSuggestions() {
    var open = document.querySelectorAll ? document.querySelectorAll('.rqd-fqc__suggest') : [];
    Array.prototype.forEach.call(open, function (m) { if (m.parentNode) m.parentNode.removeChild(m); });
  }

  /* ── the live half of the apply gate ─────────────────────────────────────────────────────────
     ⛔ APPLY MUST LIGHT FROM WHAT IS ON SCREEN, NOT FROM WHAT THE SERVER LAST SAID. The button's
     label and counts come from the baked record, which only updates after a row save round-trips.
     Left that way you would fill in the carrier, the last thing the gate wants, and watch the
     button sit grey until you clicked somewhere else. With the floppy gone that is the difference
     between the card working and the card looking broken. */
  function domRowIsSet(card, kind, idx) {
    var costRaw = fieldVal(card, kind, idx, 'cost');
    var costEntered = costRaw != null && costRaw !== '' && isFinite(parseFloat(String(costRaw).replace(/[$,\s]/g, '')));
    if (!costEntered) return false;
    var tBtn = card.querySelector('[data-fqc-action="cycle-type"][data-fqc-kind="' + kind + '"][data-fqc-idx="' + idx + '"]');
    var type = tBtn ? tBtn.dataset.fqcType : '';
    if (!type || type === 'unset') return false;
    if (kind !== 'freight') return true;   // install has no carrier clause; see rowPrompts
    var carrier = fieldVal(card, kind, idx, 'carrier');
    return !!(carrier && carrier.trim());
  }

  /* Paint the two red prompts for one row straight off the gate's failing clauses. */
  function refreshRowPrompts(card, kind, idx) {
    var row = rowEl(card, kind, idx);
    if (!row) return;
    var costRaw = fieldVal(card, kind, idx, 'cost');
    var costEntered = costRaw != null && costRaw !== '' && isFinite(parseFloat(String(costRaw).replace(/[$,\s]/g, '')));
    var tBtn = card.querySelector('[data-fqc-action="cycle-type"][data-fqc-kind="' + kind + '"][data-fqc-idx="' + idx + '"]');
    var carrierEl = fieldEl(card, kind, idx, 'carrier');
    var needType = costEntered && (!tBtn || !tBtn.dataset.fqcType || tBtn.dataset.fqcType === 'unset');
    var needCarrier = kind === 'freight' && costEntered && !!carrierEl && !String(carrierEl.value || '').trim();
    if (tBtn && tBtn.classList) tBtn.classList.toggle('rqd-fqc__needs', !!needType);
    if (carrierEl && carrierEl.classList) carrierEl.classList.toggle('rqd-fqc__needs', !!needCarrier);
  }

  /* ⛔ EDITING AN APPLIED ROW UN-TICKS IT IMMEDIATELY, AND IT HAS TO.
     A disabled button never fires a click, so if an applied row kept its tick while being edited,
     Apply would stay disabled and the only way to re-enable it would be to click some OTHER
     control first — a dead end you would hit by doing the obvious thing. Dropping the tick on the
     first keystroke is also simply TRUE: the number on screen is no longer the number on the
     customer's quote. The server reaches the same answer on the next save, by signature. */
  function refreshApply(card, kind) {
    var rows = card.querySelectorAll('.rqd-fqc__breakdown');
    var setCount = 0, appliedCount = 0, pending = 0, total = 0;
    Array.prototype.forEach.call(rows, function (row) {
      var el = row.querySelector('[data-fqc-field]');
      if (!el || el.dataset.fqcKind !== kind) return;
      var idx = parseInt(el.dataset.fqcIdx, 10);
      if (!(idx >= 0)) return;
      total++;
      var isSet = domRowIsSet(card, kind, idx);
      var isApplied = row.classList.contains('rqd-toggle--done');
      if (isSet) setCount++;
      if (isApplied) appliedCount++;
      if (isSet && !isApplied) pending++;
    });
    var applyBtn = card.querySelector('[data-fqc-action="apply"][data-fqc-kind="' + kind + '"]');
    if (!applyBtn) return;
    var al = applyLabelFor({
      setCount: setCount, totalCount: total,
      appliedCount: appliedCount, pendingCount: pending,
    });
    applyBtn.textContent = al.label;
    applyBtn.disabled = !al.ready;
    applyBtn.className = 'rqd-invoice__link rqd-fqc-apply' + (al.ready ? '' : ' rqd-invoice__generate');
  }

  function unapplyRowLocally(card, kind, idx) {
    var row = rowEl(card, kind, idx);
    if (!row || !row.classList || !row.classList.contains('rqd-toggle--done')) return;
    row.classList.remove('rqd-toggle--done');
    row.dataset.state = 'ready';
    var chk = row.querySelector('.rqd-toggle__check');
    if (chk) chk.innerHTML = '';
  }

  function handleInput(target) {
    if (!target || !target.matches) return false;
    var card = target.closest ? target.closest('.rqd-fqc') : null;
    if (!card) return false;
    var field = target.dataset && target.dataset.fqcField;
    if (field !== 'cost' && field !== 'markup' && field !== 'charged' && field !== 'fixed') return false;
    var kind = target.dataset.fqcKind;
    var idx = parseInt(target.dataset.fqcIdx, 10);
    if (!kind || !(idx >= 0)) return false;
    /* CUSTOM_FIXEDCOST_BY_CLAUDE - a typed fixed cost is sent on the save; a held price stays held (the markup moves). */
    var _brow = target.closest('.rqd-fqc__breakdown');
    if (field === 'fixed' && _brow) _brow.dataset.fqcFixedTouched = '1';
    /* CUSTOM_FREIGHTKEEPIFSENT_BY_CLAUDE (ONYX 6e7486c559b9) - only a markup somebody TYPED is sent; a cost or fee edit
       leaves the price to the worker, which keeps a price the customer was already sent. */
    if (field === 'markup' && _brow) _brow.dataset.fqcMarkupTyped = '1';
    var _driver = field === 'charged' ? 'charged'
      : (field === 'fixed' && _brow && _brow.dataset.fqcLast === 'charged' ? 'charged' : 'markup');
    recalcRow(card, kind, idx, _driver);
    syncHoldBtn(card, kind, idx);
    unapplyRowLocally(card, kind, idx);
    refreshRowPrompts(card, kind, idx);
    refreshApply(card, kind);
    return true;
  }

  /* The carrier box is not part of the cost/markup/charged arithmetic, but it IS the second half of
     the apply gate, so typing in it has to move the red outline and the button in the same way. */
  function handleCarrierInput(target) {
    if (!target || !target.dataset) return false;
    if (target.dataset.fqcField !== 'carrier' && target.dataset.fqcField !== 'name') return false;
    var card = target.closest ? target.closest('.rqd-fqc') : null;
    if (!card) return false;
    var kind = target.dataset.fqcKind;
    var idx = parseInt(target.dataset.fqcIdx, 10);
    if (!kind || !(idx >= 0)) return false;
    unapplyRowLocally(card, kind, idx);
    refreshRowPrompts(card, kind, idx);
    refreshApply(card, kind);
    return true;
  }

  /* ── auto-save when focus leaves the ROW ──────────────────────────────────────────────────
     Ryan 2026-08-29: typing in the fields and picking a carrier should save on its own; Apply is
     what makes the numbers official.

     ⛔ ON LEAVING THE ROW, NOT EACH FIELD, and the difference is not stylistic. Per-field blur-save
     would (a) repaint the panel between two fields — rerenderDetail blurs the active element by
     design, so tabbing cost → markup would throw you straight back out of markup; (b) fire before a
     cost exists, and both this handler and /freight-cost-save refuse a save without one, so filling
     the carrier first (which is what people do, reading off the supplier's email) would error; and
     (c) write a deal-feed note per field, burying the record people read days later.

     ⚠️ AND IT DOES NOT REPAINT. A background save calls patchCardChrome instead of afterMutate —
     the row already shows what was typed, and rewriting innerHTML underneath somebody now typing in
     the NEXT row is the same focus-stealing bug in a different costume. */
  /* Did the RECORD hold a cost for this row? Read from the saved quote rather than from the box,
     because the box is what has just been emptied. */
  function rowHadCost(card, kind, idx) {
    var q = quoteFor(card.dataset.ref);
    if (!q) return false;
    if (kind === 'freight') {
      var r = rowsFor(q)[idx];
      return !!(r && (r.costEntered || n2(r.cost) > 0));
    }
    var c = ((q.wsInstallation && q.wsInstallation.companies) || [])[idx];
    return !!(c && n2(c.cost) > 0);
  }

  function rowSignature(card, kind, idx) {
    var tBtn = card.querySelector('[data-fqc-action="cycle-type"][data-fqc-kind="' + kind + '"][data-fqc-idx="' + idx + '"]');
    return [
      fieldVal(card, kind, idx, 'cost'), fieldVal(card, kind, idx, 'fixed'), fieldVal(card, kind, idx, 'markup'), // CUSTOM_FIXEDCOST_BY_CLAUDE
      fieldVal(card, kind, idx, 'charged'), fieldVal(card, kind, idx, 'carrier'),
      fieldVal(card, kind, idx, 'notes'), fieldVal(card, kind, idx, 'name'),
      tBtn ? tBtn.dataset.fqcType : '',
    ].join('|');
  }

  /* Repaint only what a save can change: the row's ✓, the header counter and the Apply control. */
  function patchCardChrome(card, kind, q) {
    var st = cardState(kind, q);
    var titleEl = card.querySelector('.rqd-detail__section-title');
    if (titleEl) {
      var sum = titleEl.querySelector('.rqd-section-title__summary');
      if (sum) sum.textContent = st.counter;
      var tick = titleEl.querySelector('.rqd-detail__section-done');
      if (st.done && !tick && document.createElement) {
        var mk = document.createElement('span');
        mk.className = 'rqd-detail__section-done';
        mk.textContent = '✓';
        titleEl.appendChild(mk);
      } else if (!st.done && tick && tick.parentNode) {
        tick.parentNode.removeChild(tick);
      }
      if (titleEl.classList) titleEl.classList.toggle('rqd-detail__section-title--done', !!st.done);
    }
    var applyBtn = card.querySelector('[data-fqc-action="apply"]');
    if (applyBtn) {
      var al = applyLabelFor(st);
      applyBtn.textContent = al.label;
      applyBtn.disabled = !al.ready;
      applyBtn.className = 'rqd-invoice__link rqd-fqc-apply' + (al.ready ? '' : ' rqd-invoice__generate');
    }
    /* The lede too — a save can move a row into "set", which changes what Apply would put on the
       quote, and the figure beside it is that number. */
    var applyRow = card.querySelector('.rqd-fqc__apply-row');
    if (applyRow) {
      var amt = applyRow.querySelector('.rqd-toggle__date');
      var blk = st.block || {};
      if (amt) amt.textContent = money(n2(blk.appliedTotalQuoted != null ? blk.appliedTotalQuoted : blk.totalQuoted));
      var lede = applyRow.querySelector('span');
      if (lede) lede.textContent = (st.appliedCount > 0 && st.pendingCount === 0 && st.synced)
        ? 'Customer has been quoted' : 'Customer will be quoted';
    }
    /* ⛔ `st.rows`, NOT `st.standalone` — THE PATCH MUST INDEX THE SAME ARRAY THE RENDER DID.
       renderDealFreightCard does `rows.forEach(function (r, i) { ... data-fqc-idx="i" ... })` over
       the FULL row list, folded vendors included. Reading st.standalone here meant the two index
       spaces diverged the moment anything was folded:

         rendered:  ECORE(0) BSFS(1) Nautilus(2) Stairmaster(3,folded) TKO(4) Lock-Jaw(5)
         patched:   ECORE(0) BSFS(1) Nautilus(2) TKO(3)                Lock-Jaw(4)

       so on RW-1351-B a quiet save ticked TKO's row from LOCK-JAW's state and never touched
       Lock-Jaw's at all. Invisible on a full repaint, which reindexes correctly — it only showed
       between a background save and the next render, which is the window this whole patch path
       lives in. A folded row renders no cost input, so fieldEl returns null and it is skipped
       here on its own; it needs no special case, only the right array. */
    var list = kind === 'freight' ? st.rows : st.companies;
    (list || []).forEach(function (r, i) {
      var el = fieldEl(card, kind, i, 'cost');
      var row = el && el.closest ? el.closest('.rqd-fqc__breakdown') : null;
      if (!row || !row.classList) return;
      /* ⛔ THE TICK IS `applied`, NOT `isSet` — it means "on the customer's quote". Ryan: pressing
         Apply is what ticks the row. */
      var isSet = kind === 'freight' ? !!r.isSet : !!(r && r.type && r.type !== 'unset');
      var isApplied = rowApplied(r, (st.block || {}).appliedAt, isSet);
      row.classList.toggle('rqd-toggle--done', isApplied);
      row.dataset.state = isApplied ? 'applied' : (isSet ? 'ready' : 'not-recorded');
      var chk = row.querySelector('.rqd-toggle__check');
      if (chk) chk.innerHTML = isApplied ? '&#10003;' : '';
      refreshRowPrompts(card, kind, i);
    });
    refreshApply(card, kind);
  }

  function handleFocusOut(target, related) {
    hideSuggestions();
    if (!target || !target.closest) return false;
    var row = target.closest('.rqd-fqc__breakdown');
    if (!row) return false;
    /* Still inside the same row — tabbing between its own boxes is not "done with this row".
       A null relatedTarget means focus left the document entirely, which IS done. */
    if (related && row.contains && row.contains(related)) return false;
    var card = row.closest('.rqd-fqc');
    if (!card) return false;
    var el = row.querySelector('[data-fqc-field]');
    if (!el) return false;
    var kind = el.dataset.fqcKind;
    var idx = parseInt(el.dataset.fqcIdx, 10);
    if (!kind || !(idx >= 0)) return false;
    if (row.dataset.fqcSaving === '1') return false;
    /* Nothing typed → nothing to save. Without this, merely tabbing through a card would POST every
       row it passed and write a deal-feed note for each. */
    var sig = rowSignature(card, kind, idx);
    if (row.dataset.fqcSnap === undefined) { row.dataset.fqcSnap = sig; return false; }
    if (row.dataset.fqcSnap === sig) return false;
    /* A row that never had a cost has nothing to save — skip quietly rather than shouting at
       somebody who typed a carrier and moved on. ⛔ BUT AN EMPTIED BOX THAT DID HOLD A NUMBER IS A
       DELETION AND MUST GO THROUGH. Skipping it unconditionally is what left a wrong figure in the
       record with an empty box on screen, and put it back on the next render. */
    if (moneyVal(card, kind, idx, 'cost') == null && !rowHadCost(card, kind, idx)) return false;
    row.dataset.fqcSaving = '1';
    var settle = function () {
      row.dataset.fqcSaving = '';
      row.dataset.fqcSnap = rowSignature(card, kind, idx);
    };
    var p = (kind === 'freight')
      ? saveFreightRow(card, card.dataset.ref, idx, null, true)
      : saveInstallRow(card, card.dataset.ref, idx, null, null, true);
    Promise.resolve(p).then(settle, settle);
    return true;
  }

  /* Remember what a row looked like when focus arrived, so leaving it can tell "edited" from
     "walked past". */
  function handleFocusIn(target) {
    if (!target || !target.closest) return false;
    var row = target.closest('.rqd-fqc__breakdown');
    if (!row) return false;
    var card = row.closest('.rqd-fqc');
    var el = row.querySelector('[data-fqc-field]');
    if (!card || !el) return false;
    var idx = parseInt(el.dataset.fqcIdx, 10);
    if (!(idx >= 0)) return false;
    row.dataset.fqcSnap = rowSignature(card, el.dataset.fqcKind, idx);
    /* CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-08-30) — the ↺ Hold target, snapshotted the moment
       a COST box is entered and not before. Ported from the QW's `freightHoldTargets`, which takes
       its snapshot on the same event and for the same reason: once typing starts, `recalcRow` has
       already moved the charged figure, so there is nothing left to remember.
       ⚠️ Only on the cost box. Focusing markup or charged means the person is driving the price
       deliberately; offering to undo that is noise. ⚠️ And only once per visit — re-snapshotting
       on a second focus would capture the value the first edit produced and Hold would restore the
       wrong number. */
    if (target.dataset && target.dataset.fqcField === 'cost' && row.dataset.fqcHold === undefined) {
      var _chEl = fieldEl(card, el.dataset.fqcKind, idx, 'charged');
      var _ch = _chEl ? parseFloat(String(_chEl.value || '').replace(/[$,\s]/g, '')) : NaN;
      if (isFinite(_ch) && _ch > 0) row.dataset.fqcHold = String(_ch);
    }
    syncHoldBtn(card, el.dataset.fqcKind, idx);
    /* Clicking the box offers what we have used before, with nothing typed yet (Ryan 2026-08-29). */
    if (suggestKeyFor(target)) showSuggestions(target);
    return true;
  }

  async function saveFreightRow(card, ref, idx, btn, quiet) {
    var q = quoteFor(ref);
    var rows = q ? rowsFor(q) : [];
    var row = rows[idx];
    if (!row) return;
    /* ⛔ AN EMPTIED BOX THAT USED TO HOLD A NUMBER IS A DELETION, NOT A NO-OP. Ryan, 2026-08-29,
       on a made-up $500 test figure: *"I should be able to delete the $500 … and it should remember
       the new value on page load, but it's remembering $500 and keeping it there."* It was — the
       row skipped its save entirely, so the record kept the old cost and the next render put it
       back on screen. Nothing said so, which made it look like the save was broken rather than
       never attempted.

       ⚠️ CLEARED IS NOT ZERO. An explicit $0 is a decision ("this supplier does not bill freight")
       that passes the Apply gate; a cleared row has no number and must fail it. The endpoint keeps
       the two apart with `clearCost`, and so does this. */
    var cost = moneyVal(card, 'freight', idx, 'cost');
    var clearing = false;
    if (cost == null) {
      var hadCost = !!(row.costEntered || n2(row.cost) > 0);
      if (!hadCost) return;              // never had one — nothing to save and nothing to delete
      clearing = true;
      cost = 0;
    }
    /* Committing the row is what teaches the box a name — not typing it. A name abandoned
       mid-edit should not come back as a suggestion tomorrow. */
    rememberName('carrier', fieldVal(card, 'freight', idx, 'carrier'));
    var payload = {
      quoteRef: ref,
      vendor: row.name || row._label,
      cost: cost,
      clearCost: clearing,
      carrier: fieldVal(card, 'freight', idx, 'carrier') || '',
      type: (fieldEl(card, 'freight', idx, 'cost') && card.querySelector('[data-fqc-action="cycle-type"][data-fqc-kind="freight"][data-fqc-idx="' + idx + '"]')
        ? card.querySelector('[data-fqc-action="cycle-type"][data-fqc-kind="freight"][data-fqc-idx="' + idx + '"]').dataset.fqcType
        : row.type) || 'unset',
      notes: fieldVal(card, 'freight', idx, 'notes') || '',
    };
    /* Markup or charged — never both. The row records which box was touched last so the server is
       told the same thing the Workstation would infer, rather than being left to pick. */
    var mr = rowEl(card, 'freight', idx);
    /* CUSTOM_FIXEDCOST_BY_CLAUDE - only a box somebody typed in is sent; an empty one means "use the supplier fee". */
    var _bk = mr && mr.closest ? (mr.closest('.rqd-fqc__breakdown') || mr) : mr;
    if (_bk && _bk.dataset && _bk.dataset.fqcFixedTouched === '1') {
      var _fx = moneyVal(card, 'freight', idx, 'fixed');
      payload.fixedCost = (_fx == null) ? null : _fx;
    }
    if (mr && mr.dataset.fqcLast === 'charged') {
      var ch = moneyVal(card, 'freight', idx, 'charged');
      if (ch != null) payload.charged = ch;
    } else {
      /* CUSTOM_FREIGHTKEEPIFSENT_BY_CLAUDE (ONYX 6e7486c559b9, Ryan 2026-10-17, RW-1513-A) - the markup box is sent only
         when somebody typed in it, or the row has no markup stored yet (the painted default is the intent). A cost or fee
         edit alone sends neither, so the worker's rule decides: a price the customer was already sent stays, the markup
         absorbs it; an unsent price follows the cost. */
      var _mkTyped = !!(mr && mr.dataset.fqcMarkupTyped === '1');
      var _mkStored = parseFloat(row.markup);
      if (_mkTyped || !isFinite(_mkStored)) {
        var mk = parseFloat(String(fieldVal(card, 'freight', idx, 'markup') || '').replace(/[%\s]/g, ''));
        if (isFinite(mk)) payload.markup = mk;
      }
    }
    busy(btn, true);
    try {
      var resp = await API.post('/freight-cost-save', payload);
      if (!resp || !resp.success) throw new Error((resp && resp.error) || 'unknown error');
      if (mr) delete mr.dataset.fqcMarkupTyped;
      /* CUSTOM_FREIGHTKEEPIFSENT_BY_CLAUDE - the worker kept the price: show the kept price and the markup that absorbed it
         now, not the figure the box worked out while the cost was being typed. */
      if (resp.chargePin && resp.chargePin.pin) {
        var _pinCh = fieldEl(card, 'freight', idx, 'charged');
        var _pinMk = fieldEl(card, 'freight', idx, 'markup');
        if (_pinCh && isFinite(Number(resp.chargePin.charged))) _pinCh.value = Number(resp.chargePin.charged).toFixed(2);
        if (_pinMk && isFinite(Number(resp.chargePin.markup))) _pinMk.value = String(resp.chargePin.markup);
      }
      /* CUSTOM_FREIGHTCHARGEPIN_BY_CLAUDE (Ryan 2026-09-19) — ⛔ SAY IT WHEN THE PRICE WAS REFUSED.
         `/freight-cost-save` has returned `chargePin` since the pin shipped (2026-09-16) and this
         card read NONE of it, so on a paid deal a typed price was accepted by the UI, saved, quietly
         rejected server-side, and put back by the next poll ten to twenty seconds later — no error,
         no explanation, the number just undoing itself.

         RW-1317-C, 2026-09-19: Ryan typed -100 into the markup, watched it stick, watched it revert;
         typed 0 into the charged box, watched the button flip to "Applied" by itself, watched that
         revert too. Four attempts before anyone looked at the response. The pin's own header says a
         price that silently refuses to move is worse than one that moves — it writes the reason to
         the deal feed and hands it back here, and this was the half that was missing.

         ⚠️ ONLY WHEN THE CALLER'S PRICE WAS ACTUALLY REJECTED (`refuseCallerPrice`), not on every
         pin: an automatic keep on an unpaid deal is the rail working quietly and correctly, and a
         dialog on each of those would train people to click through the one that matters.
         ⚠️ AFTER the patches below would be too late to matter and before them would race the
         repaint, so it is awaited HERE, with the row's own values still on screen. */
      if (resp.chargePin && resp.chargePin.refuseCallerPrice) {
        await rqdSay('The price you typed was not saved. ' + (resp.chargePin.why
          ? (resp.chargePin.why.charAt(0).toUpperCase() + resp.chargePin.why.slice(1)) + '.'
          : 'This deal has already been paid.')
          + ' The quoted price stays at ' + money(n2(resp.chargePin.charged)) + '.'
          + '\n\nThe supplier cost you entered WAS saved. To change what a paid customer is charged,'
          + ' use Adjust Order — or, to correct freight that should never have moved, the Quote'
          + ' Workstation.');
      }
      if (q) {
        if (resp.freightQuotes) q.freightQuotes = resp.freightQuotes;
        patchFreightFinancials(q, resp.wsFreight);
        /* A background save patches the card in place; an explicit one repaints, which is also the
           confirmation an explicit action owes the person who took it. */
        if (quiet) patchCardChrome(card, 'freight', q); else afterMutate(ref, q);
        if (notePendingPatch && resp.wsFreight) {
          var _ws = resp.wsFreight;
          notePendingPatch('fqc-freight:' + ref, ref,
            function (qq) { patchFreightFinancials(qq, _ws); },
            function (qq) {
              var srv = qq && qq.wsFreight;
              return !!srv && n2(srv.totalCost) === n2(_ws.totalCost);
            });
        }
      }
      if (fetchQuotesQuiet) fetchQuotesQuiet();
    } catch (err) {
      busy(btn, false);
      /* An auto-save failure still has to be said -- a silent one leaves the admin believing a
         number is recorded that is not. It just does not steal focus with a dialog while they are
         mid-sentence in the next row; the row keeps its unsaved values either way. */
      await rqdSay('Could not save the freight quote: ' + (err.message || err));
    }
  }

  /* ⛔ ADDING AN INSTALLER INSERTS ONE ROW; IT DOES NOT REPAINT THE PANEL. Ryan 2026-08-29:
     *"when I click add installer, the cards all shake up and down a little... like what used to
     happen when I checked a checkbox."* Same cause as that one — afterMutate rewrites the whole
     deal panel's innerHTML, its height collapses and rebuilds inside a frame, and the browser
     clamps and restores scrollTop.

     ⚠️ SAFE HERE ONLY BECAUSE AN ADD IS APPEND-ONLY. The new company lands at the END of the array,
     so no existing row's `data-fqc-idx` moves. REMOVING one is the opposite — every row after it
     shifts down — and `data-fqc-idx` indexes the array that saveInstallRow re-reads, so a stale
     index there would write one installer's numbers onto another. Remove keeps its full repaint on
     purpose. */
  function insertInstallRowInPlace(card, q, i) {
    var addRow = card.querySelector('.rqd-fqc__add-row');
    if (!addRow || !addRow.parentNode || !document.createElement) return false;
    var st = installState(q);
    var c = st.companies[i];
    if (!c) return false;
    var tmp = document.createElement('div');
    tmp.innerHTML = renderInstallCompanyRows(q, c, i);
    while (tmp.firstChild) addRow.parentNode.insertBefore(tmp.firstChild, addRow);
    /* The add row follows the new count: it becomes "Second installer", and disappears at the cap. */
    if (st.companies.length >= MAX_INSTALLERS) {
      addRow.parentNode.removeChild(addRow);
    } else {
      var label = addRow.querySelector('span');
      if (label) label.textContent = st.companies.length ? 'Second installer' : 'Add an installer';
      var btn = addRow.querySelector('[data-fqc-action="add-installer"]');
      if (btn) btn.dataset.fqcIdx = String(st.companies.length);
    }
    patchCardChrome(card, 'install', q);
    return true;
  }

  async function saveInstallRow(card, ref, idx, btn, extra, quiet) {
    var q = quoteFor(ref);
    rememberName('installer', fieldVal(card, 'install', idx, 'name'));
    var payload = Object.assign({ ref: ref, index: idx }, extra || {});
    if (!extra || !extra.remove) {
      payload.name = fieldVal(card, 'install', idx, 'name') || '';
      /* ⛔ AN EMPTIED BOX THAT HELD A NUMBER SENDS AN EXPLICIT 0, IT DOES NOT OMIT THE FIELD.
         Absence means "leave it" to this endpoint, so omitting it is what left a deleted cost
         sitting in the record and put it back on the next render — the same bug Ryan hit on the
         freight side with a $500 test figure.
         ⚠️ Install has no `_costEntered`, so 0 IS its cleared state — there is no "$0 is a real
         quote" distinction to preserve here the way there is on freight, because
         installApplyBlockers gates on the mark alone. */
      var cost = moneyVal(card, 'install', idx, 'cost');
      if (cost != null) payload.cost = cost;
      else if (rowHadCost(card, 'install', idx)) payload.cost = 0;
      var tBtn = card.querySelector('[data-fqc-action="cycle-type"][data-fqc-kind="install"][data-fqc-idx="' + idx + '"]');
      payload.type = (tBtn && tBtn.dataset.fqcType) || 'unset';
      payload.notes = fieldVal(card, 'install', idx, 'notes') || '';
      var mr = rowEl(card, 'install', idx);
      if (mr && mr.dataset.fqcLast === 'charged') {
        var ch = moneyVal(card, 'install', idx, 'charged');
        if (ch != null) payload.charged = ch;
      } else {
        var mk = parseFloat(String(fieldVal(card, 'install', idx, 'markup') || '').replace(/[%\s]/g, ''));
        if (isFinite(mk)) payload.markup = mk;
      }
    }
    busy(btn, true);
    try {
      var resp = await API.post('/install-cost-save', payload);
      if (!resp || !resp.success) throw new Error((resp && resp.error) || 'unknown error');
      if (q) {
        patchInstallFinancials(q, resp.wsInstallation);
        if (quiet) patchCardChrome(card, 'install', q); else afterMutate(ref, q);
        if (notePendingPatch && resp.wsInstallation) {
          var _wi = resp.wsInstallation;
          notePendingPatch('fqc-install:' + ref, ref,
            function (qq) { patchInstallFinancials(qq, _wi); },
            function (qq) {
              var srv = qq && qq.wsInstallation;
              return !!srv && n2(srv.totalCost) === n2(_wi.totalCost);
            });
        }
      }
      if (fetchQuotesQuiet) fetchQuotesQuiet();
    } catch (err) {
      busy(btn, false);
      await rqdSay('Could not save the installer: ' + (err.message || err));
    }
  }

  /* ⛔ A TICKBOX MUST NOT REPAINT THE PANEL. Ryan 2026-08-29: *"each time I check and uncheck a box
     it results in the entire screen moving up and then down a few pixels."*

     This used to call afterMutate, which calls rerenderDetail — and that rewrites the innerHTML of
     the WHOLE deal detail panel, every card in it, not just this one. The panel's height collapses
     and rebuilds inside a frame, the browser clamps and restores scrollTop, and the page lurches.
     That is why it reads as the whole screen moving rather than one card.

     📌 AND IT BOUGHT NOTHING. Nothing on screen derives from these flags: `deliveryConditions` is
     read in exactly one place in this file (the checked states, below) and otherwise only by the
     email template. Not the header counter, not the Apply gate, not the totals, not the row ticks.
     Install `scope` touches two things and they are patched here by hand.

     Same rule the row auto-save already follows — a background mutation patches in place, only an
     explicit action repaints. */
  function patchFlagChrome(card, kind, input, key, value) {
    /* The `--on` class is belt-and-braces alongside `:has(:checked)`; the browser has already
       flipped the input itself, so only the class needs saying. Without this the class goes stale
       on any browser that is relying on it rather than on :has(). */
    var label = input && input.closest ? input.closest('.rqd-fqc__check') : null;
    if (label && label.classList) label.classList.toggle('rqd-fqc__check--on', !!value);
    if (kind !== 'install') return;
    /* Install scope is the one flag with visible consequences: a `data-state` on the row and the
       "covers the whole job" hint. ⛔ Scope is ONE string, so ticking one box clears the other —
       the sibling's class has to be cleared here or two boxes read as ticked until the next repaint
       (the structural exclusion holds in the record, not in the DOM we just left behind). */
    var row = input && input.closest ? input.closest('.rqd-fqc__flags') : null;
    if (!row) return;
    var scope = (key === 'whole' || !value) ? '' : key;
    row.dataset.state = scope || 'full';
    /* ⛔ SYNC ALL THREE FROM THE RESULTING SCOPE, don't just clear the siblings. Unticking a limit
       has to TICK Whole job, which "clear everyone else" cannot express — and the label the click
       landed on is not necessarily the one that ends up checked. */
    var boxes = row.querySelectorAll('.rqd-fqc__check');
    Array.prototype.forEach.call(boxes, function (l) {
      var inp = l.querySelector('input[data-fqc-action="install-scope"]');
      if (!inp) return;
      var k = inp.dataset.fqcKey;
      var on = (k === 'whole') ? !scope : (scope === k);
      inp.checked = on;
      if (l.classList) l.classList.toggle('rqd-fqc__check--on', on);
    });
  }

  async function setFlag(card, ref, kind, key, value, input) {
    var q = quoteFor(ref);
    var body = kind === 'freight'
      ? { ref: ref, kind: 'freight', flag: key, value: value }
      /* Three boxes, one field. 'whole' IS the empty scope, and unticking either limit falls back
         to it — so every path that is not "tick a limit" resolves to ''. The server's allow-list
         (INSTALL_SCOPES) never sees the word 'whole'. */
      : { ref: ref, kind: 'install', scope: (key === 'whole' || !value) ? '' : key };
    if (input) input.disabled = true;
    try {
      var resp = await API.post('/logistics-flags-set', body);
      if (!resp || !resp.success) throw new Error((resp && resp.error) || 'unknown error');
      if (q) {
        if (resp.wsFreight) q.wsFreight = resp.wsFreight;
        if (resp.wsInstallation) q.wsInstallation = resp.wsInstallation;
      }
      patchFlagChrome(card, kind, input, key, value);
    } catch (err) {
      if (input) { input.checked = !value; }
      await rqdSay('Could not save that setting: ' + (err.message || err));
    } finally {
      /* ⚠️ RE-ENABLED IN `finally`, NOT ONLY ON THE ERROR PATH. It used to be left disabled on
         success because the repaint replaced the element outright; with no repaint, the element
         survives, and a box you could tick once and never untick is the bug that swap introduces. */
      if (input) input.disabled = false;
    }
  }

  /* ⛔ Reuses RQD.alert when it exists. These modules load BEFORE dashboard-main.js in document
     order, so the export is checked at CALL time rather than captured at load — the trap the
     native-dialog conversion brief calls out. Falls back to alert() only if main never booted,
     which means the page is broken anyway and a silent failure would hide it. */
  function rqdSay(msg) {
    if (window.RQD && typeof window.RQD.alert === 'function') return window.RQD.alert(msg);
    alert(msg);
    return Promise.resolve();
  }
  function rqdAsk(msg, opts) {
    if (window.RQD && typeof window.RQD.confirm === 'function') return window.RQD.confirm(msg, opts);
    return Promise.resolve(confirm(msg));
  }

  /* One entry point, called from main's delegated click router.

     ⛔ THE DECISION IS SYNCHRONOUS AND THE WORK IS NOT, and they have to be separated. The router
     does `if (handle(e.target)) return;` — an `async` function returns a Promise, and a Promise is
     always truthy, so an async handler would claim EVERY click including the ones it declines and
     silently swallow the rest of the router. This wrapper answers "is this mine?" with a real
     boolean and lets the work run on its own; each path reports its own failure to the user. */
  /* The actions that are genuinely CLICKS. The tickboxes, the two selects and the kind dropdown
     carry data-fqc-action too but are driven by `change`, and a click router that claimed them
     would swallow the event for everything downstream while doing nothing itself. */
  /* ⛔ NO 'save'. The floppy is gone (Ryan 2026-08-29): a row saves itself when focus leaves it,
     and Apply is the explicit commit. Leaving 'save' here would claim clicks for a button that no
     longer exists, which costs nothing today and silently swallows the next control given that
     name. */
  /* ⛔ THIS LIST IS THE ROUTER. An action missing from it reaches `runAction` never — the branch
     below sits there looking correct and does nothing, with no error. `hold` joined it 2026-08-30. */
  var CLICK_ACTIONS = ['cycle-type', 'contact-menu', 'ask', 'split', 'apply', 'add-installer', 'remove-installer', 'hold'];

  /* Wait out every row that is mid-save or has unsaved edits in this card. Returns when the record
     behind it can be trusted. Rows are saved SEQUENTIALLY, not in parallel: /freight-cost-save
     read-modify-writes one workstation_meta record, so two in flight at once is a lost update. */
  async function flushPendingRows(card) {
    var rows = card.querySelectorAll ? card.querySelectorAll('.rqd-fqc__breakdown') : [];
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var el = row.querySelector('[data-fqc-field]');
      if (!el) continue;
      var kind = el.dataset.fqcKind;
      var idx = parseInt(el.dataset.fqcIdx, 10);
      if (!kind || !(idx >= 0)) continue;
      /* Mid-flight from the focus-out handler — let it land rather than starting a second one. */
      if (row.dataset.fqcSaving === '1') {
        var waited = 0;
        while (row.dataset.fqcSaving === '1' && waited < 8000) {
          await new Promise(function (r) { setTimeout(r, 50); });
          waited += 50;
        }
        continue;
      }
      var sig = rowSignature(card, kind, idx);
      if (row.dataset.fqcSnap === sig) continue;          // nothing typed since focus arrived
      if (moneyVal(card, kind, idx, 'cost') == null && !rowHadCost(card, kind, idx)) continue;
      row.dataset.fqcSaving = '1';
      try {
        if (kind === 'freight') await saveFreightRow(card, card.dataset.ref, idx, null, true);
        else await saveInstallRow(card, card.dataset.ref, idx, null, null, true);
      } catch (_) { /* the save says its own piece; Apply must still be allowed to refuse below */ }
      row.dataset.fqcSaving = '';
      row.dataset.fqcSnap = rowSignature(card, kind, idx);
    }
  }

  /* ⛔ `:not(.rqd-fqc__suggest)` IS LOad-BEARING, NOT TIDINESS. The suggestion list borrows
     `.rqd-fqc__menu` for its LOOK, which quietly made that class a teardown selector as well as a
     styling one: this ran on the capture-phase click and hid the list the instant after focusin
     opened it. The symptom was exact and misleading — clicking the box showed nothing, typing
     showed the list, because typing fires no click behind it (Ryan 2026-08-29). */
  function closeAllContactMenus() {
    var open = document.querySelectorAll ? document.querySelectorAll('.rqd-fqc__menu:not(.rqd-fqc__suggest)') : [];
    Array.prototype.forEach.call(open, function (m) { m.hidden = true; });
    /* ⚠️ ANY button that owns one of these menus, not just this card's own. The activity card's
       ✉ Email pill uses the same .rqd-fqc__menu-wrap markup (Ryan 2026-08-29) but is routed by
       dashboard-main, so keying the reset on data-fqc-action left its aria-expanded stuck at
       "true" after an outside click had already hidden the menu — the state a screen reader
       reads, silently disagreeing with the one on screen. */
    var btns = document.querySelectorAll
      ? document.querySelectorAll('[data-fqc-action="contact-menu"], .rqd-fqc__menu-wrap [aria-haspopup="true"]')
      : [];
    Array.prototype.forEach.call(btns, function (b) { b.setAttribute('aria-expanded', 'false'); });
  }

  function handleAction(target) {
    var btn = target && target.closest ? target.closest('[data-fqc-action]') : null;
    if (!btn) return false;
    if (CLICK_ACTIONS.indexOf(btn.dataset.fqcAction) === -1) return false;
    if (!btn.closest('.rqd-fqc')) return false;
    /* ⚠️ A FLOATING PROMISE NEEDS A CATCH. Every branch inside runAction handles its own failure,
       but an unexpected throw on the way to one of them (a missing dep, a bad dataset) would
       otherwise land as an unhandled rejection: the button stays busy, nothing appears on screen,
       and the admin's only evidence is the console. Say something instead. */
    runAction(btn).catch(function (err) {
      rqdSay('Something went wrong on that control: ' + ((err && err.message) || err));
    });
    return true;
  }

  async function runAction(btn) {
    var action = btn.dataset.fqcAction;
    var card = btn.closest('.rqd-fqc');
    if (!card) return false;
    var ref = card.dataset.ref;
    var idx = parseInt(btn.dataset.fqcIdx, 10);
    var kind = btn.dataset.fqcKind;

    if (action === 'cycle-type') {
      /* Advance in the DOM and save immediately — the toggle IS the decision, and leaving it
         unsaved until the row was left would let it read "official" while the record
         still says unset, which is the state the Apply gate reads. */
      var next = TYPE_CYCLE[btn.dataset.fqcType] || 'estimate';
      btn.dataset.fqcType = next;
      btn.textContent = TYPE_GLYPH[next];
      btn.className = 'rqd-fqc__type rqd-fqc__type--' + next;
      btn.title = TYPE_TITLE[next];
      /* The mark IS a gate clause, so flipping it has to clear its own red outline and re-evaluate
         Apply before the save round-trips — same reason the boxes do. */
      unapplyRowLocally(card, kind, idx);
      refreshRowPrompts(card, kind, idx);
      refreshApply(card, kind);
      /* ⛔ QUIET — patch, do not repaint. Same lurch as the tickboxes (Ryan 2026-08-29): a full
         rerenderDetail rewrites the whole panel and the page jumps. And it tells this control
         nothing it does not already know — the four lines above have just set the glyph, class,
         title and dataset by hand. What the save legitimately changes is the row tick, the header
         counter and the Apply button, which is exactly what patchCardChrome updates. */
      if (kind === 'freight') await saveFreightRow(card, ref, idx, null, true);
      else await saveInstallRow(card, ref, idx, null, null, true);
      return true;
    }

    if (action === 'add-installer') {
      /* Quiet, then insert the one new row by hand — see insertInstallRowInPlace. If anything
         about the card is not what this expects, fall back to the repaint rather than leaving a
         saved installer with no row on screen. */
      await saveInstallRow(card, ref, idx, btn, { type: 'unset' }, true);
      var qa = quoteFor(ref);
      if (!qa || !insertInstallRowInPlace(card, qa, idx)) { if (qa) afterMutate(ref, qa); }
      return true;
    }

    if (action === 'remove-installer') {
      if (!(await rqdAsk('Its cost comes off the deal.', {
        title: 'Remove this installer?', confirmLabel: 'Remove', cancelLabel: 'Keep it', danger: true,
      }))) return true;
      await saveInstallRow(card, ref, idx, btn, { remove: true });
      return true;
    }

    /* ⛔ THE MENU OPENS SYNCHRONOUSLY AND CLOSES EVERY OTHER ONE. Two expanded deals put two
       menus in the document; leaving a stale one open under a second card is the kind of thing
       that reads as the button not working. */
    if (action === 'contact-menu') {
      var wrap = btn.closest('.rqd-fqc__menu-wrap');
      var menu = wrap && wrap.querySelector('.rqd-fqc__menu');
      var wasOpen = menu && !menu.hidden;
      closeAllContactMenus();
      if (menu && !wasOpen) {
        menu.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
      }
      return true;
    }

    if (action === 'ask') {
      closeAllContactMenus();
      var vendor = btn.dataset.vendor;
      var askKind = btn.dataset.fqcAsk || 'freight';
      var H = window.DashboardHandshake;
      if (!H) { await rqdSay('The request composer has not loaded yet — reload the page and try again.'); return true; }
      /* ⛔ bucket OMITTED, not null-as-a-bucket. Buckets are derived from onlineLineItems and do not
         exist on an offline deal; the handshake's own signature treats a falsy bucket as the legacy
         per-vendor path, which is the one the Workstation shares. */
      if (askKind === 'dims') await H.requestDims(ref, vendor);
      else if (askKind === 'both') {
        /* ⛔ 'both' IS A COMPOSITION, NOT A THIRD `kind`. freightQuotes[].kind is read as a strict
           binary in ~20 places across six theme and six worker modules, and THREE of them reject an
           unknown value outright rather than defaulting — /vendor-email-routes throws, and the
           shipping card's `hasRequestRow` goes false and the request row disappears. So this sends
           ONE email carrying both asks, stamps kind 'freight' as it always did, and records the
           dims half in its own field. Every existing reader keeps working untouched. */
        if (typeof H.requestBoth === 'function') await H.requestBoth(ref, vendor);
        else await H.request(ref, vendor);
      } else await H.request(ref, vendor);
      return true;
    }

    /* Give every supplier folded into this one its own shipment back. */
    if (action === 'split') {
      var q2 = quoteFor(ref);
      var rows2 = q2 ? rowsFor(q2) : [];
      var primary = rows2[idx];
      if (!primary) return true;
      var partners = rows2.filter(function (o) { return o.linkedTo && o.linkedTo === primary.name; });
      if (!partners.length) return true;
      busy(btn, true);
      try {
        /* ⚠️ SEQUENTIALLY. /freight-cost-save read-modify-writes one workstation_meta record, so
           two in flight at once is a lost update. */
        for (var pi = 0; pi < partners.length; pi++) {
          var resp2 = await API.post('/freight-cost-save', {
            quoteRef: ref, vendor: partners[pi].name || partners[pi]._label,
            /* ⚠️ Its OWN stored cost goes back, not zero. Folding zeroes a vendor's contribution to
               the totals, not the number recorded against it, so a split returns whatever it was
               carrying — which for a vendor folded before it was ever priced is $0, and that is the
               truth rather than a loss. */
            cost: n2(partners[pi].cost), linkedTo: null,
          });
          if (!resp2 || !resp2.success) throw new Error((resp2 && resp2.error) || 'unknown error');
          if (q2 && resp2.wsFreight) patchFreightFinancials(q2, resp2.wsFreight);
        }
        if (q2) afterMutate(ref, q2);
      } catch (err) {
        busy(btn, false);
        await rqdSay('Could not split that shipment: ' + (err.message || err));
      }
      return true;
    }

    /* CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-08-30) — ↺ HOLD. Put the charged figure back and
       let the markup absorb the new cost, so the customer keeps the number they were given.

       ⛔ IT DRIVES THE EXISTING 'charged' PATH RATHER THAN INVENTING ONE. Setting the box and
       running `recalcRow(..., 'charged')` back-solves the markup on screen AND stamps the row
       `fqcLast='charged'`, which is what makes the save post `charged` — and the server turn that
       into `_chargedOverride`, the same flag the Workstation's Hold sets. One meaning for "held",
       written in one place.

       ⚠️ NO SAVE FROM HERE, DELIBERATELY. The button sits inside `.rqd-fqc__breakdown`, so
       `handleFocusOut` treats the click as still-in-this-row and does not fire — the row saves when
       focus genuinely leaves it, or when Apply calls `flushPendingRows`. Saving here as well would
       post the row twice and write two deal-feed notes for one decision.

       ⚠️ THE SNAPSHOT IS CONSUMED. Leaving it would keep the button live against a target that has
       just been restored, so it would offer to undo itself. */
    if (action === 'hold') {
      var _hRow = btn.closest('.rqd-fqc__breakdown');
      var _hTarget = _hRow ? parseFloat(_hRow.dataset.fqcHold) : NaN;
      var _hCh = fieldEl(card, kind, idx, 'charged');
      if (!_hRow || !_hCh || !isFinite(_hTarget)) return true;
      _hCh.value = _hTarget.toFixed(2);
      recalcRow(card, kind, idx, 'charged');
      delete _hRow.dataset.fqcHold;
      syncHoldBtn(card, kind, idx);
      refreshRowPrompts(card, kind, idx);
      refreshApply(card, kind);
      return true;
    }

    if (action === 'apply') {
      if (btn.disabled) return true;
      /* ⛔ FLUSH THE ROW BEFORE ASKING THE SERVER WHAT IT HOLDS, AND THIS IS A MONEY PATH.
         Clicking Apply IS a focus-out from whatever row was being typed in, so the row's own
         auto-save POST and this apply fire in the same instant — and /logistics-apply re-reads KV
         server-side. On a row that was ALREADY set, Apply is enabled from the old state, so
         changing a cost 500 -> 800 and clicking Apply could put 500 on the customer's quote.

         ⚠️ A brand-new row is protected by luck (Apply is disabled until the server has seen it);
         an EDIT is not, which is the case that matters and the one nobody would reproduce by
         accident. Waiting is the whole fix — and it is also what makes "the click saves it"
         literally true rather than nearly true. */
      await flushPendingRows(card);
      var q = quoteFor(ref);
      var block = kind === 'freight' ? (q && q.wsFreight) : (q && q.wsInstallation);
      var amount = money(n2(block && block.totalQuoted));
      /* ⛔ THIS SENTENCE IS TRUE AS OF 2026-08-29 AND WAS NOT BEFORE. It has always promised the
         draft order was updated; /logistics-apply did not touch it and returned draftSynced:false
         with a modal saying the opposite — two dialogs, one click, opposite claims. Apply now
         rebuilds the draft, so the promise holds and the caveat modal fires only on the
         exceptions (paid order, >100 lines, a goods-price disagreement).
         "Payment link" rather than "draft order": it is the same object, and the link is the part
         anybody reading this dialog actually cares about. */
      if (!(await rqdAsk('This puts ' + amount + ' on the customer’s quote and updates the payment link. The rep is emailed that it is ready.', {
        title: 'Apply ' + (kind === 'freight' ? 'freight' : 'installation') + ' to ' + ref + '?',
        confirmLabel: 'Apply to the quote', cancelLabel: 'Not yet',
      }))) return true;
      busy(btn, true);
      try {
        var resp = await API.post('/logistics-apply', { ref: ref, kind: kind });
        if (!resp || !resp.success) throw new Error((resp && resp.error) || 'unknown error');
        if (q) {
          if (resp.wsFreight) patchFreightFinancials(q, resp.wsFreight);
          if (resp.wsInstallation) patchInstallFinancials(q, resp.wsInstallation);
          /* ⚠️ AFTER the two patches, deliberately: as of 2026-08-30 this carries the BUILT row's
             financials, which is the only object that has `grandTotal` — the figure Order Total
             renders. The patches above move the fee ROW; this is what moves the TOTAL under it. */
          if (resp.financials) q.financials = Object.assign({}, q.financials, resp.financials);
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-30) — ⛔ AND THE ROW'S OWN AMOUNT MOVES
             WITH IT. `q.total` is what the board's Amount column reads and the builder sets it from
             the same `_grandTotal` as `financials.grandTotal`. Leaving it behind would fix the card
             and leave the row beside it disagreeing for the length of a poll — a narrower version
             of the bug this change exists to remove. ⚠️ Guarded on `!= null` because the server
             OMITS it when the rebuild did not land, and `undefined` must mean "keep what we have"
             rather than blank the Amount. */
          if (resp.total != null) q.total = resp.total;
          afterMutate(ref, q);
        }
        /* ⛔ THE REP IS TOLD BY THE SAME ENDPOINT THE WORKSTATION USES, unchanged. Ryan's call:
           the rep should learn their freight came back the same way whichever surface the admin
           used. /freight-ready-notify refuses unless status is 'quoted' with a total, which the
           apply above has just made true, and it already suppresses a self-notify — so an admin
           applying their own deal gets nothing.
           ⚠️ Its failure must not read as the APPLY failing: the numbers are already written and
           re-applying to "fix" the email would push a second history version. */
        if (kind === 'freight') {
          try {
            await API.post('/freight-ready-notify', {
              ref: ref,
              totalAmount: n2(resp.totalQuoted),
              vendorCount: ((resp.wsFreight && resp.wsFreight.vendors) || []).filter(function (v) { return !v.linkedTo; }).length,
            });
          } catch (_) { /* applied fine; only the heads-up email did not go */ }
        }
        if (fetchQuotesQuiet) fetchQuotesQuiet();
        /* ⛔ SAID OUT LOUD RATHER THAN LEFT AS AN ABSENCE. The quote PDF, the Financials card, GP
           and commission are correct now; the Shopify DRAFT behind the pay link is not, because
           only a Workstation save rebuilds its line items (handleSendQuote invoices the draft as it
           stands). An admin who applied here and then sent a payment link would send the old
           amount, and nothing on screen would have told them. */
        if (resp.draftSynced === false && resp.draftNote) await rqdSay(resp.draftNote);
      } catch (err) {
        busy(btn, false);
        await rqdSay('Could not apply it to the quote: ' + (err.message || err));
      }
      return true;
    }

    return false;
  }

  /* Change-events, not clicks — the two <select>s and the tickboxes. Separate entry point so main's
     click router stays a click router. Same sync-decision / async-work split as handleAction, and
     for the same reason. */
  function handleChange(target) {
    var card = target && target.closest ? target.closest('.rqd-fqc') : null;
    if (!card) return false;
    /* The markup/charged memo is pure DOM bookkeeping — no request, so it is done inline and
       deliberately reports NOT handled, letting anything else that cares still see the event. */
    if (target.matches('.rqd-fqc__markup') || target.matches('.rqd-fqc__charged')) {
      var mrow = target.closest('.rqd-fqc__breakdown');
      if (mrow) mrow.dataset.fqcLast = target.matches('.rqd-fqc__charged') ? 'charged' : 'markup';
      return false;
    }
    if (!target.matches('[data-fqc-action="delivery-flag"]')
      && !target.matches('[data-fqc-action="install-scope"]')
      && !target.matches('[data-fqc-action="set-linked"]')
      /* ⛔ THE WHITELIST IS THE ROUTER. A control the change handler does not name here reaches
         runChange never — the branch below it would sit there looking correct and doing nothing. */
      && !target.matches('[data-fqc-action="add-to-shipment"]')) return false;
    runChange(target, card).catch(function (err) {
      rqdSay('Something went wrong on that control: ' + ((err && err.message) || err));
    });
    return true;
  }

  async function runChange(target, card) {
    var ref = card.dataset.ref;

    if (target.matches('[data-fqc-action="delivery-flag"]')) {
      await setFlag(card, ref, 'freight', target.dataset.fqcKey, target.checked, target);
      return true;
    }
    if (target.matches('[data-fqc-action="install-scope"]')) {
      await setFlag(card, ref, 'install', target.dataset.fqcKey, target.checked, target);
      return true;
    }
    /* The primary's "Add to shipment": folds the CHOSEN vendor into this row, which is the same
       write as set-linked with the two ends swapped. */
    if (target.matches('[data-fqc-action="add-to-shipment"]')) {
      var pIdx = parseInt(target.dataset.fqcIdx, 10);
      var pq = quoteFor(ref);
      var pRows = pq ? rowsFor(pq) : [];
      var primary = pRows[pIdx];
      var chosenName = target.value;
      if (!primary || !chosenName) return true;
      var chosen = pRows.filter(function (o) { return o.name === chosenName; })[0];
      if (!chosen) return true;
      try {
        var rr = await API.post('/freight-cost-save', {
          quoteRef: ref, vendor: chosen.name || chosen._label,
          cost: n2(chosen.cost), linkedTo: primary.name,
        });
        if (!rr || !rr.success) throw new Error((rr && rr.error) || 'unknown error');
        if (pq) { patchFreightFinancials(pq, rr.wsFreight); afterMutate(ref, pq); }
      } catch (err) {
        await rqdSay('Could not add that supplier to the shipment: ' + (err.message || err));
      }
      return true;
    }

    if (target.matches('[data-fqc-action="set-linked"]')) {
      var idx = parseInt(target.dataset.fqcIdx, 10);
      var q = quoteFor(ref);
      var rows = q ? rowsFor(q) : [];
      var row = rows[idx];
      if (!row) return true;
      try {
        var resp = await API.post('/freight-cost-save', {
          quoteRef: ref, vendor: row.name || row._label,
          cost: n2(row.cost), linkedTo: target.value || null,
        });
        if (!resp || !resp.success) throw new Error((resp && resp.error) || 'unknown error');
        if (q) { patchFreightFinancials(q, resp.wsFreight); afterMutate(ref, q); }
      } catch (err) {
        await rqdSay('Could not change the shipment: ' + (err.message || err));
      }
      return true;
    }
    return false;
  }

  // ── Public API ─────────────────────────────────────────────────────────────────────────────
  window.RQD.handleFreightCardAction = handleAction;
  /* One entry point: the money boxes drive the arithmetic, the carrier and installer-name boxes
     drive the apply gate. Both have to move the red prompts and the button, so main routes every
     `input` here rather than the card exporting two hooks main would have to remember to call. */
  window.RQD.handleFreightCardInput = function (target, evt) {
    /* Completion runs BEFORE the gate is re-read, so the red outline and the button react to the
       previewed name rather than to the three letters that were typed. */
    inlineComplete(target, evt);
    var a = handleInput(target);
    var b = handleCarrierInput(target);
    if (suggestKeyFor(target)) showSuggestions(target);
    return a || b;
  };
  window.RQD.handleFreightCardFocusIn = handleFocusIn;
  window.RQD.handleFreightCardFocusOut = handleFocusOut;
  window.RQD.handleFreightCardChange = handleChange;
  /* ⛔ THE MENU NEEDS AN UNCONDITIONAL EXIT. Everything else on these cards is routed through
     main's delegated handlers, but "click anywhere else to dismiss" has no target to delegate from
     — a popover whose only exit is pressing the same button again is a small trap, and Escape is
     the exit people reach for first. Attached ONCE at init, capture-phase, so it runs before the
     click router decides anything. */
  var _menuDismissBound = false;
  function bindMenuDismiss() {
    if (_menuDismissBound || !document.addEventListener) return;
    _menuDismissBound = true;
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!(t && t.closest && t.closest('.rqd-fqc__menu-wrap'))) closeAllContactMenus();
      /* The suggestion list survives a click on the box that owns it — that click is what opened
         it — and on its own items, whose mousedown handler runs first. Anything else dismisses. */
      var onOwnField = !!(t && t.dataset && suggestKeyFor(t));
      var inList = !!(t && t.closest && t.closest('.rqd-fqc__suggest'));
      if (!onOwnField && !inList) hideSuggestions();
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeAllContactMenus(); hideSuggestions(); }
    });
  }

  window.RQD.initFreightCards = function (deps) {
    esc                = deps.esc;
    money              = deps.money;
    formatDate         = deps.formatDate;
    sectionTitleHtml   = deps.sectionTitleHtml;
    getIsAdmin         = deps.getIsAdmin;
    API                = deps.API;
    getAllQuotes       = deps.getAllQuotes;
    rerenderDetail     = deps.rerenderDetail;
    refreshProgressBar = deps.refreshProgressBar;
    notePendingPatch   = deps.notePendingPatch;
    vendorNamesMatch   = deps.vendorNamesMatch;
    fetchQuotesQuiet   = deps.fetchQuotesQuiet;
    buildToggle        = deps.buildToggle;
    bindMenuDismiss();
  };
  window.RQD.renderDealFreightCard = renderDealFreightCard;
  window.RQD.renderDealInstallCard = renderDealInstallCard;
  /* Exported for the render harness and for the emission site, which must not draw a wrapper around
     nothing. Self-gating render + an exported gate is the same contract renderClaimCard uses. */
  window.RQD.dealLogisticsCardsApply = cardsApply;
  window.RQD._fqcRowsFor = rowsFor;
})();
